import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { analyticsService, userService, programService, authService, infoSessionService, partnerDiscountService, advisorService, testimonialService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { StudentProgress } from '../components/StageProgress';
import { usePipelineStages } from '../services/usePipelineStages';
import { Program, InfoSession, ProgramPricingPathway, ProgramPricingResponse, AdvisorThread } from '../types';
import PathwaySelectionBuilder from './PathwaySelectionBuilder';
import { ContactVerificationManager, VerificationChannel } from '../services/contactVerification';
import { PhoneField } from '../components/PhoneField';
import PasswordRequirements from '../components/PasswordRequirements';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { applyTheme, getStoredTheme } from '../utils/theme';
import {
  connectAdvisorSocket,
  emitAdvisorStopTyping,
  emitAdvisorTyping,
  joinAdvisorThreadRoom,
  leaveAdvisorThreadRoom,
  onAdvisorMessageEvent,
  onAdvisorStopTypingEvent,
  onAdvisorThreadUpdatedEvent,
  onAdvisorTypingEvent,
} from '../services/socket';
import { env } from '../env';

const SMS_DISABLED = env.REACT_APP_SMS_DISABLED === 'true';
const PHONE_VALIDATION_DISABLED = env.REACT_APP_DISABLE_PHONE_VALIDATION === 'true' || SMS_DISABLED;
const isPhoneFormatValid = (value?: string | null) => {
  if (!value) return false;
  return PHONE_VALIDATION_DISABLED ? true : isValidPhoneNumber(value);
};


// Helper to map backend API data to the frontend UI (add color, etc.)
const STAGE_COLORS: Record<string, string> = {
  visitor: 'bg-gray-400',
  prospect: 'bg-blue-400',
  applicant: 'bg-yellow-400',
  orientation: 'bg-green-400',
  pre_departure: 'bg-indigo-400',
  active_student: 'bg-purple-400',
  alumni: 'bg-pink-400',
};

// Only when the site has a Stripe key. With none (payments off, as on staging), loadStripe('')
// throws on every page of the app, because this file is loaded with it.
const stripeKey = env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;




function getStageInfo(stage: string, pipelineStages: any[]) {
  const s = pipelineStages.find(s => s.code === stage);
  if (!s) return undefined;
  return {
    ...s,
    color: STAGE_COLORS[stage] || 'bg-gray-400',
  };
}

const formatStageTitle = (title?: string | null) => {
  if (!title) return '';
  return title.split(' - ')[0].split(' – ')[0].trim();
};

interface Application {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  password_set?: boolean;
  current_stage: string;
  updated_at?: string;
  email_verified?: boolean;
  phone_verified?: boolean;
  program_interest?: string;
  selected_program_id?: number | null;
  application_status?: string;
  accepted_at?: string | null;
  selected_cohort_ids?: number[];
  selected_extension_ids?: number[];
  pathway_selection_confirmed?: boolean;
  passport_photo_key?: string | null;
  passport_photo_url?: string | null;
  application_fee_amount?: number;
  application_fee_currency?: string;
  application_fee_paid?: boolean;
  payments_total?: number;
  // Add all the questionnaire fields for completion checking
  date_of_birth?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  education_level?: string;
  field_of_study?: string;
  institution?: string;
  gpa?: string;
  motivation?: string;
  special_requirements?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
  guardian_name?: string;
  guardian_phone?: string;
  guardian_email?: string;
  guardian_relationship?: string;
  phone?: string;
  commitment_level?: string;
  timeline?: string;
  how_did_you_hear?: string;
  program_motivation?: string;
  application_link?: string;
  step_completion?: Record<string, boolean | string>;
  stage_history: Array<{
    from_stage: string | null;
    to_stage: string;
    transition_date: string;
    notes?: string;
  }>;
}

type DreamingForm = {
  first_name: string;
  last_name: string;
  date_of_birth?: string;
  phone?: string;
  email: string;
  address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  education_level?: string;
  field_of_study?: string;
  institution?: string;
  gpa?: string;
  program_interest?: string;
  commitment_level?: string;
  timeline?: string;
  how_did_you_hear?: string;
  program_motivation?: string;
  motivation?: string;
  special_requirements?: string;
  emergency_contact_name?: string;
  emergency_contact_phone?: string;
  emergency_contact_relationship?: string;
};

const formatPhoneNumberForDisplay = (value?: string) => value || '';

const normalizeDateInput = (value?: string) => {
  if (!value) return '';
  const match = value.match(/^\d{4}-\d{2}-\d{2}/);
  if (match) return match[0];
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

type AccountSetupMode = 'password' | 'verification' | 'all';

interface AccountSetupModalProps {
  open: boolean;
  onClose: () => void;
  application: Application;
  canEdit: boolean;
  onSaved?: () => Promise<void> | void;
  userId?: number;
  mode?: AccountSetupMode;
}

const ApplicationFormSection: React.FC<{ title: string; description?: string; children: React.ReactNode }> = ({
  title,
  description,
  children,
}) => (
  <section className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/80 backdrop-blur px-5 py-4 space-y-4 shadow-sm">
    <div>
      <p className="text-xs uppercase tracking-[0.3em] text-emerald-500">{title}</p>
      {description && <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">{description}</p>}
    </div>
    {children}
  </section>
);

const PaymentElementForm: React.FC<{
  onPaid: (paymentIntentId?: string) => Promise<void>;
  submitting: boolean;
  setSubmitting: (value: boolean) => void;
  canSubmit: boolean;
}> = ({ onPaid, submitting, setSubmitting, canSubmit }) => {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: {},
    });
    if (error) {
      setSubmitting(false);
      return;
    }
    await onPaid(paymentIntent?.id);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <PaymentElement />
      <button
        type="submit"
        disabled={!stripe || submitting || !canSubmit}
        className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400"
      >
        {submitting ? 'Processing...' : 'Pay Application Fee'}
      </button>
    </form>
  );
};

const PaymentModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onPaid: () => Promise<void>;
}> = ({ open, onClose, onPaid }) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentId, setPaymentId] = useState<number | null>(null);
  const [amount, setAmount] = useState<number | null>(null);
  const [currency, setCurrency] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consentChecked, setConsentChecked] = useState(false);
  const [receiptSending, setReceiptSending] = useState(false);
  const [receiptStatus, setReceiptStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    setError(null);
    setConsentChecked(false);
    setReceiptStatus(null);
    userService.createApplicationFeeIntent()
      .then((data) => {
        if (!active) return;
        setClientSecret(data.client_secret);
        setPaymentId(data.payment_id);
        setAmount(data.amount);
        setCurrency(data.currency);
      })
      .catch((err: any) => {
        if (!active) return;
        setError(err?.response?.data?.error || 'Unable to start payment.');
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [open]);

  if (!open) return null;

  const appearance = {
    theme: 'stripe' as const,
    variables: {
      colorPrimary: '#059669',
      colorText: '#0f172a',
      colorBackground: '#ffffff',
      borderRadius: '8px',
      fontFamily: 'Inter, system-ui, sans-serif',
    },
  };

  const displayAmount = amount != null && currency
    ? new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount)
    : null;
  const termsUrl = env.REACT_APP_PAYMENT_TERMS_URL;
  const termsVersion = env.REACT_APP_PAYMENT_TERMS_VERSION || 'v1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-3xl bg-slate-50/90 dark:bg-slate-900/95 shadow-2xl max-h-[90vh] overflow-y-auto overscroll-contain">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4 dark:border-slate-700">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-emerald-500 mb-2">Stage 3 · Application Fee</p>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Pay your application fee</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {displayAmount ? `Application fee: ${displayAmount}` : 'Confirm your payment details to continue.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition"
            aria-label="Close payment modal"
          >
            ✕
          </button>
        </div>
        <div className="px-6 py-6">
          {loading && <p className="text-sm text-slate-500 dark:text-slate-300">Loading payment details...</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {!loading && !error && !clientSecret && (
            <div className="space-y-3">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                Your application fee has already been paid.
              </div>
              {receiptStatus && (
                <div className="text-xs text-emerald-600 dark:text-emerald-300">{receiptStatus}</div>
              )}
              <button
                type="button"
                onClick={async () => {
                  setReceiptSending(true);
                  setReceiptStatus(null);
                  try {
                    await userService.resendApplicationFeeReceipt();
                    setReceiptStatus('Receipt sent. Please check your email.');
                  } catch (err: any) {
                    setReceiptStatus(err?.response?.data?.error || 'Unable to send receipt.');
                  } finally {
                    setReceiptSending(false);
                  }
                }}
                disabled={receiptSending}
                className="w-full rounded-lg border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-200 dark:hover:bg-emerald-900/30 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {receiptSending ? 'Sending receipt...' : 'Resend receipt'}
              </button>
            </div>
          )}
          {!loading && !error && clientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret: clientSecret ?? undefined, appearance }}>
              <div className="space-y-4">
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-4 py-3 text-sm text-slate-700 dark:text-slate-200">
                  <p className="font-semibold text-slate-900 dark:text-white">Payment authorization</p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-300">
                    You authorize Muslim Study Abroad to charge your payment method for the application fee.
                  </p>
                  {termsUrl && (
                    <a
                      href={termsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-flex text-xs font-semibold text-emerald-600 hover:text-emerald-700 dark:text-emerald-300"
                    >
                      View Enrollment & Refund Policy →
                    </a>
                  )}
                  <label className="mt-3 flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300">
                    <input
                      type="checkbox"
                      checked={consentChecked}
                      onChange={(event) => setConsentChecked(event.target.checked)}
                      className="mt-0.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span>
                      I agree to the Enrollment & Refund Policy{termsUrl ? '' : ' (ask advisor for a copy)'} and authorize this payment.
                    </span>
                  </label>
                </div>
                <PaymentElementForm
                  submitting={submitting}
                  setSubmitting={setSubmitting}
                  canSubmit={consentChecked}
                  onPaid={async (paymentIntentId) => {
                    try {
                      await userService.confirmStudentPayment({
                        payment_id: paymentId ?? undefined,
                        payment_intent_id: paymentIntentId,
                        consented: true,
                        terms_version: termsVersion,
                      });
                      await onPaid();
                      onClose();
                    } catch (err: any) {
                      setError(err?.response?.data?.error || 'Unable to confirm payment.');
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                />
                {!consentChecked && (
                  <p className="text-xs text-amber-600 dark:text-amber-300">
                    Please confirm the authorization to enable payment.
                  </p>
                )}
              </div>
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
};

interface DreamingQuestionnaireModalProps {
  open: boolean;
  onClose: () => void;
  application: Application;
  canEdit: boolean;
  onSaved?: () => Promise<void> | void;
  onAdvance?: () => Promise<void>;
  canAdvanceStage?: boolean;
  programOptions: Program[];
}

interface ApplicationFormModalProps {
  open: boolean;
  onClose: () => void;
  application: Application;
  onSubmit: (data: Record<string, string>) => Promise<void>;
  submitting: boolean;
  onSaveDraft?: (data: Record<string, string>) => Promise<void>;
  draftSaving?: boolean;
  confirmed?: boolean;
}

interface ModalStepFooterProps {
  confirmLabel: string;
  confirmChecked: boolean;
  onConfirmChange: (checked: boolean) => void;
  warning?: string | null;
  onCancel: () => void;
  cancelLabel?: string;
  onSave?: () => Promise<void> | void;
  saveLabel?: string;
  saveDisabled?: boolean;
  onSubmit: () => Promise<void> | void;
  submitLabel: string;
  submitDisabled: boolean;
}

interface PathwaySelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => Promise<void> | void;
  onSubmitSelection: () => Promise<void> | void;
  saving: boolean;
  confirmed: boolean;
  programOptions: Program[];
  selection: {
    programId: number | null;
    trackIds: number[];
    addonIds: number[];
  };
  onSelectionChange: (selection: { programId: number | null; trackIds: number[]; addonIds: number[] }) => void;
}

interface InfoSessionModalProps {
  open: boolean;
  onClose: () => void;
  onCompleted: () => void;
  sessions: InfoSession[];
  loading: boolean;
  error?: string | null;
  onRegister: (sessionId: number) => Promise<void>;
  onCancel: (sessionId: number) => Promise<void>;
}

interface ObjectionToolkitModalProps {
  open: boolean;
  onClose: () => void;
  onCompleted: () => void;
  programId?: number | null;
}

interface FinancingModalProps {
  open: boolean;
  onClose: () => void;
  onCompleted: () => void;
}

const ModalStepFooter: React.FC<ModalStepFooterProps> = ({
  confirmLabel,
  confirmChecked,
  onConfirmChange,
  warning,
  onCancel,
  cancelLabel = 'Cancel',
  onSave,
  saveLabel = 'Save & Continue Later',
  saveDisabled,
  onSubmit,
  submitLabel,
  submitDisabled,
}) => (
  <div className="border-t border-slate-200 px-6 py-4 dark:border-slate-700">
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
        <input
          type="checkbox"
          checked={confirmChecked}
          onChange={(event) => onConfirmChange(event.target.checked)}
          className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
        />
        {confirmLabel}
      </label>
      {warning && (
        <span className="text-xs text-amber-600 dark:text-amber-300">{warning}</span>
      )}
    </div>
    <div className="mt-4 flex items-center justify-end gap-3">
      {onSave && (
        <button
          type="button"
          onClick={onSave}
          disabled={saveDisabled}
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-200 dark:hover:bg-emerald-900/30 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saveLabel}
        </button>
      )}
      <button
        type="button"
        onClick={onCancel}
        className="inline-flex items-center justify-center px-4 py-2 border border-slate-200 dark:border-slate-700 text-sm font-medium rounded-lg text-slate-700 dark:text-slate-100 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700"
      >
        {cancelLabel}
      </button>
      <button
        type="button"
        onClick={onSubmit}
        disabled={submitDisabled}
        className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400"
      >
        {submitLabel}
      </button>
    </div>
  </div>
);

const AccountSetupModal: React.FC<AccountSetupModalProps> = ({
  open,
  onClose,
  application,
  canEdit,
  onSaved,
  userId,
  mode = 'all',
}) => {
  const [status, setStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [verificationState, setVerificationState] = useState({ emailVerified: false, phoneVerified: false });
  const [verificationInputs, setVerificationInputs] = useState<Record<VerificationChannel, string>>({
    email: '',
    phone: ''
  });
  const [verificationUi, setVerificationUi] = useState<Record<VerificationChannel, { showCodeEntry: boolean; verifying: boolean }>>({
    email: { showCodeEntry: false, verifying: false },
    phone: { showCodeEntry: false, verifying: false }
  });
  const [verificationCooldowns, setVerificationCooldowns] = useState<Record<VerificationChannel, number>>({
    email: 0,
    phone: 0
  });
  const [sendingVerification, setSendingVerification] = useState<VerificationChannel | null>(null);
  const [lastSentChannel, setLastSentChannel] = useState<VerificationChannel | null>(null);
  const [lastSentTarget, setLastSentTarget] = useState('');
  const verificationManager = useMemo(() => (userId ? new ContactVerificationManager(userId) : null), [userId]);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordSetLocal, setPasswordSetLocal] = useState(Boolean(application.password_set));
  const [isPasswordEditing, setIsPasswordEditing] = useState(!application.password_set);
  const [phoneValue, setPhoneValue] = useState(application.phone || '');
  const initialPhoneRef = useRef(application.phone || '');
  const showPassword = mode !== 'verification';
  const showVerification = mode !== 'password';
  const headerTitle =
    mode === 'password'
      ? 'Set Your Password'
      : mode === 'verification'
        ? 'Verify Contact Info'
        : 'Set Your Password';
  const headerDescription =
    mode === 'password'
      ? 'Set a password so you can log back in anytime.'
      : mode === 'verification'
        ? (SMS_DISABLED ? 'Verify your email so we can reach you.' : 'Verify your email and phone so we can reach you.')
        : (SMS_DISABLED ? 'Set a password and verify your email to continue.' : 'Set a password and verify your contact info to continue.');

  const handleCodeInputChange = (channel: VerificationChannel, value: string) => {
    const digitsOnly = value.replace(/\D/g, '').slice(0, 6);
    setVerificationInputs(prev => ({
      ...prev,
      [channel]: digitsOnly
    }));
  };

  const updateVerificationUi = (channel: VerificationChannel, updates: Partial<{ showCodeEntry: boolean; verifying: boolean }>) => {
    setVerificationUi(prev => ({
      ...prev,
      [channel]: {
        ...prev[channel],
        ...updates,
      },
    }));
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setVerificationCooldowns(prev => ({
        email: Math.max(prev.email - 1, 0),
        phone: Math.max(prev.phone - 1, 0)
      }));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (open) {
      setStatus(null);
      setVerificationState({
        emailVerified: Boolean(application.email_verified),
        phoneVerified: Boolean(application.phone_verified)
      });
      setVerificationInputs({ email: '', phone: '' });
      setVerificationUi({
        email: { showCodeEntry: false, verifying: false },
        phone: { showCodeEntry: false, verifying: false }
      });
      setPasswordForm({ current: '', new: '', confirm: '' });
      setPasswordSetLocal(Boolean(application.password_set));
      setIsPasswordEditing(!application.password_set);
      setPhoneValue(application.phone || '');
      initialPhoneRef.current = application.phone || '';
    }
  }, [open, application]);

  const handlePhoneInputChange = (value?: string) => {
    setPhoneValue(value || '');
  };

  const isPhoneValidForVerification = Boolean(phoneValue && isPhoneFormatValid(phoneValue));

  const handlePasswordFieldChange = (field: 'current' | 'new' | 'confirm', value: string) => {
    setPasswordForm(prev => ({ ...prev, [field]: value }));
  };

  const handlePasswordSave = async () => {
    if (!canEdit) return;
    const trimmedNew = passwordForm.new.trim();
    const trimmedConfirm = passwordForm.confirm.trim();
    if (!trimmedNew || trimmedNew.length < 8) {
      setStatus({ type: 'error', text: 'Password must be at least 8 characters long.' });
      return;
    }
    if (trimmedNew !== trimmedConfirm) {
      setStatus({ type: 'error', text: 'Passwords do not match.' });
      return;
    }
    if (passwordSetLocal && !passwordForm.current.trim()) {
      setStatus({ type: 'error', text: 'Enter your current password to update it.' });
      return;
    }
    setStatus(null);
    setPasswordSaving(true);
    try {
      const payload: { new_password: string; confirm_password: string; current_password?: string } = {
        new_password: trimmedNew,
        confirm_password: trimmedConfirm
      };
      if (passwordSetLocal && passwordForm.current.trim()) {
        payload.current_password = passwordForm.current.trim();
      }
      await authService.setPassword(payload);
      setPasswordSetLocal(true);
      setPasswordForm({ current: '', new: '', confirm: '' });
      setIsPasswordEditing(false);
      setStatus({ type: 'success', text: 'Password saved successfully.' });
      onSaved?.();
    } catch (error: any) {
      const message = error.response?.data?.error || 'Unable to update password right now.';
      setStatus({ type: 'error', text: message });
    } finally {
      setPasswordSaving(false);
    }
  };

  const sendVerification = async (channel: VerificationChannel) => {
    if (!canEdit) return;
    if (channel === 'phone' && SMS_DISABLED) {
      setStatus({ type: 'error', text: 'Text verification is temporarily unavailable.' });
      return;
    }
    if (verificationCooldowns[channel] > 0) {
      setStatus({ type: 'error', text: `Please wait ${verificationCooldowns[channel]}s before requesting another ${channel} code.` });
      return;
    }
    if (!verificationManager) {
      setStatus({ type: 'error', text: 'You need to be logged in to verify your contact information.' });
      return;
    }
    if (channel === 'phone') {
      if (!isPhoneValidForVerification) {
        setStatus({ type: 'error', text: 'Enter a valid phone number with country code (e.g., +15551234567) before requesting verification.' });
        return;
      }
      try {
        await userService.updateMyApplication({ phone: phoneValue });
      } catch (error: any) {
        const message = error.response?.data?.error || 'Unable to save your phone number before verification.';
        setStatus({ type: 'error', text: message });
        return;
      }
    }
    setStatus(null);
    setSendingVerification(channel);
    try {
      await verificationManager.forChannel(channel).sendCode();
      updateVerificationUi(channel, { showCodeEntry: true });
      setVerificationCooldowns(prev => ({
        ...prev,
        [channel]: 60
      }));
      setLastSentChannel(channel);
      setLastSentTarget(channel === 'email' ? application.email : (phoneValue || ''));
      setStatus({
        type: 'success',
        text: `${channel === 'email' ? 'Email' : 'Phone'} verification code sent. Check your ${channel === 'email' ? 'inbox' : 'messages'} for the code.`,
      });
    } catch (error: any) {
      console.error('Failed to request verification', error);
      const message = error.response?.data?.error || 'Unable to send verification right now. Please try again.';
      setStatus({ type: 'error', text: message });
    } finally {
      setSendingVerification(null);
    }
  };

  const verifyCode = async (channel: VerificationChannel) => {
    if (!canEdit) return;
    if (!verificationManager) {
      setStatus({ type: 'error', text: 'You need to be logged in to verify your contact information.' });
      return;
    }
    const code = verificationInputs[channel];
    if (!code || code.length < 4) {
      setStatus({ type: 'error', text: 'Enter the verification code that was sent to you.' });
      return;
    }
    setStatus(null);
    updateVerificationUi(channel, { verifying: true });
    try {
      await verificationManager.forChannel(channel).verifyCode(code);
      setVerificationInputs(prev => ({ ...prev, [channel]: '' }));
      updateVerificationUi(channel, { showCodeEntry: false });
      setVerificationState(prev => ({
        ...prev,
        emailVerified: channel === 'email' ? true : prev.emailVerified,
        phoneVerified: channel === 'phone' ? true : prev.phoneVerified
      }));
      setStatus({ type: 'success', text: `${channel === 'email' ? 'Email' : 'Phone'} verified successfully.` });
      await onSaved?.();
    } catch (error: any) {
      console.error('Failed to verify code', error);
      const message = error.response?.data?.error || 'Unable to verify the code right now. Please try again.';
      setStatus({ type: 'error', text: message });
    } finally {
      updateVerificationUi(channel, { verifying: false });
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 px-4 py-8">
      <div className="w-full max-w-3xl max-h-full overflow-y-auto glass-panel bg-white/90 dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-2xl shadow-2xl border border-slate-100 dark:border-slate-700">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">{headerTitle}</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300">{headerDescription}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
          >
            <span className="sr-only">Close</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {!canEdit && (
            <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-800 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-200">
              You are viewing a completed stage. Answers are read-only.
            </div>
          )}
          {showPassword && (
            <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-white/60 dark:bg-slate-900/40">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs uppercase text-slate-600 dark:text-slate-200 tracking-[0.35em]">Account Password</p>
                  <p className="text-xs text-slate-500 dark:text-slate-300 mt-1">
                    Set a password so you can log back in anytime.
                  </p>
                </div>
                <span className={`text-xs font-semibold ${passwordSetLocal ? 'text-emerald-600 dark:text-emerald-300' : 'text-amber-600 dark:text-amber-300'}`}>
                  {passwordSetLocal ? 'Complete' : 'Required'}
                </span>
              </div>
              {passwordSetLocal && !isPasswordEditing && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 dark:border-emerald-600/50 dark:bg-emerald-900/20 p-4 flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-emerald-800 dark:text-emerald-200">Password secured</p>
                      <p className="text-xs text-emerald-700 dark:text-emerald-300">You can now log back in from any device.</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsPasswordEditing(true)}
                    className="text-xs font-semibold text-emerald-700 dark:text-emerald-200 hover:underline"
                  >
                    Update password
                  </button>
                </div>
              )}
              {(!passwordSetLocal || isPasswordEditing) && (
                <>
                  {passwordSetLocal && (
                    <div className="mb-3">
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase mb-1">Current Password</label>
                      <input
                        type="password"
                        value={passwordForm.current}
                        onChange={(e) => handlePasswordFieldChange('current', e.target.value)}
                        disabled={!canEdit || passwordSaving}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white dark:bg-slate-900/40 dark:text-slate-100"
                        placeholder="Current password"
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase mb-1">New Password</label>
                      <input
                        type="password"
                        value={passwordForm.new}
                        onChange={(e) => handlePasswordFieldChange('new', e.target.value)}
                        disabled={!canEdit || passwordSaving}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white dark:bg-slate-900/40 dark:text-slate-100"
                        placeholder="At least 8 characters"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase mb-1">Confirm Password</label>
                      <input
                        type="password"
                        value={passwordForm.confirm}
                        onChange={(e) => handlePasswordFieldChange('confirm', e.target.value)}
                        disabled={!canEdit || passwordSaving}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white dark:bg-slate-900/40 dark:text-slate-100"
                        placeholder="Re-enter password"
                      />
                    </div>
                  </div>
                  <PasswordRequirements
                    className="mt-3"
                    password={passwordForm.new}
                    confirm={passwordForm.confirm}
                    showConfirmMatch
                  />
                  <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:space-x-3">
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={handlePasswordSave}
                        disabled={!canEdit || passwordSaving}
                        className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md disabled:opacity-50"
                      >
                        {passwordSaving ? 'Saving…' : passwordSetLocal ? 'Update Password' : 'Save Password'}
                      </button>
                      {passwordSetLocal && (
                        <button
                          type="button"
                          onClick={() => {
                            setIsPasswordEditing(false);
                            setPasswordForm({ current: '', new: '', confirm: '' });
                          }}
                          className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-200 border border-slate-200 dark:border-slate-600 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                    <p className="mt-2 sm:mt-0 text-xs text-slate-500 dark:text-slate-300">Minimum 8 characters.</p>
                  </div>
                </>
              )}
            </div>
          )}
          
          {status && (
            <div className={`p-3 rounded-md text-sm ${status.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-800 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-200' : 'bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-200'}`}>
              {status.text}
            </div>
          )}
          {showVerification && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/50 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] uppercase tracking-[0.3em] text-slate-600 dark:text-slate-300">Email</label>
                  <span className={`text-xs font-medium ${verificationState.emailVerified ? 'text-emerald-600 dark:text-emerald-300' : 'text-amber-600 dark:text-amber-300'}`}>
                    {verificationState.emailVerified ? 'Verified' : 'Not verified'}
                  </span>
                </div>
                <input
                  type="email"
                  value={application.email}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-200 dark:border-slate-700 rounded-md bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-slate-200 text-sm"
                />
                <button
                  type="button"
                  onClick={() => sendVerification('email')}
                  disabled={
                    !canEdit ||
                    verificationState.emailVerified ||
                    sendingVerification === 'email' ||
                    verificationCooldowns.email > 0
                  }
                  className="w-full px-3 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md disabled:opacity-50"
                >
                  {verificationState.emailVerified
                    ? 'Verified'
                    : sendingVerification === 'email'
                    ? 'Sending…'
                    : verificationCooldowns.email > 0
                      ? `Resend in ${verificationCooldowns.email}s`
                      : 'Send Code'}
                </button>
                {verificationUi.email.showCodeEntry && !verificationState.emailVerified && (
                  <div className="space-y-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={verificationInputs.email}
                      onChange={(e) => handleCodeInputChange('email', e.target.value)}
                      maxLength={6}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white dark:bg-slate-900/40 dark:text-slate-100"
                      placeholder="Enter 6-digit code"
                    />
                    <button
                      type="button"
                      onClick={() => verifyCode('email')}
                      disabled={verificationUi.email.verifying}
                      className="w-full px-3 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md disabled:opacity-50"
                    >
                      {verificationUi.email.verifying ? 'Verifying…' : 'Confirm Code'}
                    </button>
                  </div>
                )}
                {lastSentChannel === 'email' && lastSentTarget && (
                  <p className="text-xs text-slate-500 dark:text-slate-300">Code sent to {lastSentTarget}</p>
                )}
              </div>
              {!SMS_DISABLED && (
                <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/50 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-[11px] uppercase tracking-[0.3em] text-slate-600 dark:text-slate-300">Phone</label>
                    <span className={`text-xs font-medium ${verificationState.phoneVerified ? 'text-emerald-600 dark:text-emerald-300' : 'text-amber-600 dark:text-amber-300'}`}>
                      {verificationState.phoneVerified ? 'Verified' : 'Not verified'}
                    </span>
                  </div>
                  <PhoneField
                    value={phoneValue || undefined}
                    onChange={handlePhoneInputChange}
                    disabled={!canEdit}
                    helperText="Include your country code."
                    containerClassName="w-full"
                    hideValidationMessage
                  />
                  <button
                    type="button"
                    onClick={() => sendVerification('phone')}
                    disabled={
                      !canEdit ||
                      (verificationState.phoneVerified && phoneValue === initialPhoneRef.current) ||
                      sendingVerification === 'phone' ||
                      verificationCooldowns.phone > 0 ||
                      !isPhoneValidForVerification
                    }
                    className="w-full px-3 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md disabled:opacity-50"
                  >
                    {verificationState.phoneVerified && phoneValue === initialPhoneRef.current
                      ? 'Verified'
                      : sendingVerification === 'phone'
                      ? 'Sending…'
                      : verificationCooldowns.phone > 0
                        ? `Resend in ${verificationCooldowns.phone}s`
                        : 'Send Code'}
                  </button>
                  {verificationUi.phone.showCodeEntry && !verificationState.phoneVerified && (
                    <div className="space-y-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={verificationInputs.phone}
                        onChange={(e) => handleCodeInputChange('phone', e.target.value)}
                        maxLength={6}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white dark:bg-slate-900/40 dark:text-slate-100"
                        placeholder="Enter 6-digit code"
                      />
                      <button
                        type="button"
                        onClick={() => verifyCode('phone')}
                        disabled={verificationUi.phone.verifying}
                        className="w-full px-3 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md disabled:opacity-50"
                      >
                        {verificationUi.phone.verifying ? 'Verifying…' : 'Confirm Code'}
                      </button>
                    </div>
                  )}
                  {lastSentChannel === 'phone' && lastSentTarget && (
                    <p className="text-xs text-slate-500 dark:text-slate-300">Code sent to {lastSentTarget}</p>
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-100 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                Close
              </button>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

const DreamingQuestionnaireModal: React.FC<DreamingQuestionnaireModalProps> = ({ open, onClose, application, canEdit, onSaved, onAdvance, canAdvanceStage, programOptions }) => {
  type CoreDreamingFields = Pick<DreamingForm, 'program_interest' | 'commitment_level' | 'timeline' | 'how_did_you_hear' | 'program_motivation'>;
  const [formData, setFormData] = useState<CoreDreamingFields>({
    program_interest: '',
    commitment_level: '',
    timeline: '',
    how_did_you_hear: '',
    program_motivation: ''
  });
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (open) {
      setStatus(null);
      setFormData({
        program_interest: application.program_interest || '',
        commitment_level: application.commitment_level || '',
        timeline: application.timeline || '',
        how_did_you_hear: application.how_did_you_hear || '',
        program_motivation: application.program_motivation || ''
      });
    }
  }, [open, application]);

  const handleChange = (field: keyof CoreDreamingFields, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const persistAnswers = async () => {
    await userService.updateMyApplication(formData);
    await onSaved?.();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit) return;
    setStatus(null);
    setSaving(true);
    try {
      await persistAnswers();
      setStatus({ type: 'success', text: 'Questionnaire saved.' });
    } catch (error: any) {
      setStatus({ type: 'error', text: error?.response?.data?.error || error?.message || 'Unable to save right now.' });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndAdvance = async () => {
    if (!canEdit) return;
    if (!canAdvanceStage) {
      setStatus({ type: 'error', text: 'Please complete each answer before continuing.' });
      return;
    }
    setStatus(null);
    setSubmitting(true);
    try {
      await persistAnswers();
      await onAdvance?.();
      setStatus({ type: 'success', text: 'Great work—moving you forward.' });
      onClose();
    } catch (error: any) {
      setStatus({ type: 'error', text: error?.response?.data?.error || error?.message || 'Unable to submit right now.' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-3 sm:px-4">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-50/95 dark:bg-slate-900/95 rounded-2xl sm:rounded-3xl shadow-2xl max-w-2xl w-full p-4 sm:p-6 space-y-5 max-h-[92vh] sm:max-h-[95vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-2">
            <p className="text-[11px] uppercase tracking-[0.25em] text-emerald-500">Stage 1 · Dreaming</p>
            <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">
              Tell us about your goals
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Five quick prompts to personalize your path.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close dreaming questionnaire"
            className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition"
          >
            ✕
          </button>
        </div>

        {!canEdit && (
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-700 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-200">
            Answers are read-only because this stage is already complete.
          </div>
        )}

        {status && (
          <div
            className={`rounded-xl border px-4 py-2 text-sm ${
              status.type === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}
          >
            {status.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <section className="space-y-4">
            <div className="space-y-4">
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-[0.2em] mb-1">
                  Program Interest
                </label>
                <select
                  value={formData.program_interest}
                  onChange={(e) => handleChange('program_interest', e.target.value)}
                  disabled={!canEdit}
                  className="w-full px-3 py-3 border border-gray-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white dark:bg-slate-800 disabled:bg-gray-100 dark:disabled:bg-slate-800 dark:text-slate-100"
                >
                  <option value="">Select a program</option>
                  {programOptions.map(program => (
                    <option key={program.id} value={program.title}>
                      {program.title}{program.location ? ` – ${program.location}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-[0.2em] mb-1">
                  Commitment Level
                </label>
                <select
                  value={formData.commitment_level}
                  onChange={(e) => handleChange('commitment_level', e.target.value)}
                  disabled={!canEdit}
                  className="w-full px-3 py-3 border border-gray-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white dark:bg-slate-800 disabled:bg-gray-100 dark:disabled:bg-slate-800 dark:text-slate-100"
                >
                  <option value="">Choose</option>
                  <option value="very_interested">Very interested</option>
                  <option value="somewhat_interested">Somewhat interested</option>
                  <option value="just_exploring">Just exploring</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-[0.2em] mb-1">
                  Timeline
                </label>
                <select
                  value={formData.timeline}
                  onChange={(e) => handleChange('timeline', e.target.value)}
                  disabled={!canEdit}
                  className="w-full px-3 py-3 border border-gray-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white dark:bg-slate-800 disabled:bg-gray-100 dark:disabled:bg-slate-800 dark:text-slate-100"
                >
                  <option value="">Choose</option>
                  <option value="next_program">Next available program</option>
                  <option value="within_6_months">Within 6 months</option>
                  <option value="flexible">Flexible / not sure</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-[0.2em] mb-1">
                  How did you hear about us?
                </label>
                <select
                  value={formData.how_did_you_hear}
                  onChange={(e) => handleChange('how_did_you_hear', e.target.value)}
                  disabled={!canEdit}
                  className="w-full px-3 py-3 border border-gray-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white dark:bg-slate-800 disabled:bg-gray-100 dark:disabled:bg-slate-800 dark:text-slate-100"
                >
                  <option value="">Choose</option>
                  <option value="friend">Friend or family</option>
                  <option value="social">Social media</option>
                  <option value="event">Event or workshop</option>
                  <option value="school">School counselor</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-200 uppercase tracking-[0.2em] mb-1">
                  What motivates you to study abroad?
                </label>
                <textarea
                  value={formData.program_motivation}
                  onChange={(e) => handleChange('program_motivation', e.target.value)}
                  disabled={!canEdit}
                  rows={4}
                  className="w-full px-3 py-3 border border-gray-300 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:bg-gray-100 dark:disabled:bg-slate-800 dark:bg-slate-900/40 dark:text-slate-100"
                  placeholder="Share your story..."
                />
              </div>
            </div>
          </section>

          {canEdit ? (
            <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save My Answers'}
              </button>
              <button
                type="button"
                disabled={submitting || !canAdvanceStage}
                onClick={handleSaveAndAdvance}
                className="inline-flex items-center justify-center px-4 py-3 border border-transparent text-sm font-medium rounded-xl text-white bg-emerald-700 hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit & Continue'}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center px-4 py-3 border border-slate-200 dark:border-slate-600 text-sm font-medium rounded-xl text-slate-700 dark:text-slate-100 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center px-4 py-2 border border-slate-200 dark:border-slate-600 text-sm font-medium rounded-md text-slate-700 dark:text-slate-100 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700"
              >
                Close
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

const ApplicationFormModal: React.FC<ApplicationFormModalProps> = ({ open, onClose, application, onSubmit, submitting, onSaveDraft, draftSaving, confirmed }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    education_level: '',
    institution: '',
    field_of_study: '',
    gpa: '',
    program_interest: '',
    timeline: '',
    commitment_level: '',
    program_motivation: '',
    motivation: '',
    how_did_you_hear: '',
    special_requirements: '',
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_phone: '',
    passport_photo_key: '',
  });
  const [status, setStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState<string | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [nameEditingEnabled, setNameEditingEnabled] = useState(false);
  const canEditNames = useMemo(() => {
    if (!application) return true;
    return !application.first_name && !application.last_name;
  }, [application]);
  const requiredComplete = useMemo(() => {
    const hasText = (value: string) => value.trim().length > 0;
    if (
      !hasText(formData.first_name)
      || !hasText(formData.last_name)
      || !hasText(formData.date_of_birth)
      || !hasText(formData.email)
      || !hasText(formData.address)
      || !hasText(formData.city)
      || !hasText(formData.state)
      || !hasText(formData.postal_code)
      || !hasText(formData.country)
      || !hasText(formData.education_level)
      || !hasText(formData.institution)
      || !hasText(formData.field_of_study)
      || !hasText(formData.gpa)
      || !hasText(formData.program_motivation)
      || !hasText(formData.motivation)
      || !hasText(formData.special_requirements)
      || !hasText(formData.emergency_contact_name)
      || !hasText(formData.emergency_contact_relationship)
      || !hasText(formData.passport_photo_key)
    ) {
      return false;
    }
    if (!formData.phone || !isPhoneFormatValid(formData.phone)) return false;
    if (!formData.emergency_contact_phone || !isPhoneFormatValid(formData.emergency_contact_phone)) return false;
    if (photoUploading) return false;
    return true;
  }, [formData, photoUploading]);

  useEffect(() => {
    if (open && application) {
      setStatus(null);
      setConfirmSubmit(Boolean(confirmed));
      setFormData({
        first_name: (application as any).first_name || '',
        last_name: (application as any).last_name || '',
        date_of_birth: normalizeDateInput(application.date_of_birth),
        email: application.email || '',
        phone: application.phone || '',
        address: application.address || '',
        city: application.city || '',
        state: application.state || '',
        postal_code: application.postal_code || '',
        country: application.country || '',
        education_level: application.education_level || '',
        institution: application.institution || '',
        field_of_study: application.field_of_study || '',
        gpa: application.gpa ? String(application.gpa) : '',
        program_interest: application.program_interest || '',
        timeline: application.timeline || '',
        commitment_level: application.commitment_level || '',
        program_motivation: application.program_motivation || '',
        motivation: application.motivation || '',
        how_did_you_hear: application.how_did_you_hear || '',
        special_requirements: application.special_requirements || '',
        emergency_contact_name: application.emergency_contact_name || '',
        emergency_contact_relationship: application.emergency_contact_relationship || '',
        emergency_contact_phone: application.emergency_contact_phone || '',
        passport_photo_key: application.passport_photo_key || '',
      });
      setPhotoPreviewUrl(application.passport_photo_url || null);
    }
  }, [open, application, confirmed]);

  if (!open) return null;

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setConfirmSubmit(false);
  };

  const handlePhotoUpload = async (file: File) => {
    if (!file) return;
    setPhotoUploadError(null);
    setPhotoUploading(true);
    const localPreview = URL.createObjectURL(file);
    setPhotoPreviewUrl(localPreview);
    try {
      const payload = await userService.getStudentPhotoUploadUrl({
        filename: file.name,
        content_type: file.type || 'image/jpeg',
      });
      await fetch(payload.upload_url, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'image/jpeg',
        },
        body: file,
      });
      setFormData((prev) => ({ ...prev, passport_photo_key: payload.key }));
      await userService.updateMyApplication({ passport_photo_key: payload.key });
    } catch (error: any) {
      setPhotoUploadError(error?.message || 'Unable to upload photo right now.');
      setPhotoPreviewUrl(application?.passport_photo_url || null);
    } finally {
      setPhotoUploading(false);
    }
  };

  const handleSubmit = async () => {
    setStatus(null);
    try {
      await onSubmit(formData);
    } catch (error: any) {
      setStatus({ type: 'error', text: error?.response?.data?.error || error?.message || 'Unable to submit application right now.' });
    }
  };

  const handleSaveDraft = async () => {
    if (!onSaveDraft) return;
    setStatus(null);
    try {
      await onSaveDraft(formData);
      setStatus({ type: 'success', text: 'Application saved. You can return anytime.' });
    } catch (error: any) {
      setStatus({ type: 'error', text: error?.response?.data?.error || error?.message || 'Unable to save right now.' });
    }
  };

  const inputClass = 'w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400';
  const textareaClass = `${inputClass} min-h-[120px] resize-y`;
  const hasText = (value: string) => value.trim().length > 0;
  const fieldBorderClass = (isValid: boolean) => (isValid ? 'field-valid' : 'field-invalid');
  const hasPhoto = Boolean(formData.passport_photo_key);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-50/90 dark:bg-slate-900/95 rounded-3xl shadow-2xl max-w-4xl w-full p-6 space-y-6 animate-fade-in-up max-h-[95vh] overflow-y-auto">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-emerald-500 mb-2">Stage 3 · Application</p>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Program Application</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">Review and complete the application so we can reserve your seat.</p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close application form"
            className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition"
          >
            ✕
          </button>
        </div>

        {status && (
          <div
            className={`rounded-xl border px-4 py-2 text-sm ${
              status.type === 'success'
                ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}
          >
            {status.text}
          </div>
        )}

        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (confirmSubmit && !submitting) {
              handleSubmit();
            }
          }}
          className="space-y-5"
        >
          <ApplicationFormSection title="Personal Information" description="This helps us match your application to your passport and academic records.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">First Name (Given Name)</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    disabled={!canEditNames && !nameEditingEnabled}
                    className={`${inputClass} ${fieldBorderClass(hasText(formData.first_name))}`}
                  />
                  {!canEditNames && (
                    <button
                      type="button"
                      onClick={() => setNameEditingEnabled((prev) => !prev)}
                      className={`inline-flex items-center justify-center w-10 h-10 rounded-lg border transition ${
                        nameEditingEnabled
                          ? 'border-emerald-400 bg-emerald-100 text-emerald-700 dark:border-emerald-500/70 dark:bg-emerald-900/40 dark:text-emerald-200'
                          : 'border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                      aria-label={nameEditingEnabled ? 'Stop editing name' : 'Edit name'}
                    >
                      ✎
                    </button>
                  )}
                </div>
                {!canEditNames && nameEditingEnabled && (
                  <p className="text-[11px] text-emerald-600 dark:text-emerald-200 mt-2">
                    Editing name to match passport.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">Last Name (Surname)</label>
                <input
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  disabled={!canEditNames && !nameEditingEnabled}
                  className={`${inputClass} ${fieldBorderClass(hasText(formData.last_name))}`}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">Date of Birth</label>
                <input
                  type="date"
                  required
                  value={formData.date_of_birth}
                  onChange={(e) => handleChange('date_of_birth', e.target.value)}
                  className={`${inputClass} ${fieldBorderClass(hasText(formData.date_of_birth))}`}
                />
              </div>
              <div>
                <PhoneField
                  label="Phone Number"
                  labelClassName="text-xs font-semibold uppercase text-slate-600 dark:text-slate-200"
                  value={formData.phone || undefined}
                  onChange={(value) => handleChange('phone', value || '')}
                  inputClassName={`${inputClass} ${fieldBorderClass(Boolean(formData.phone && isPhoneFormatValid(formData.phone)))}`}
                  helperText="Include country code for international travel updates."
                  required
                  disabled={Boolean(application?.phone)}
                  hideValidationMessage
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">Email</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  disabled={Boolean(application?.email)}
                  className={`${inputClass} ${fieldBorderClass(hasText(formData.email))}`}
                />
              </div>
            </div>
          </ApplicationFormSection>

          <ApplicationFormSection title="Contact & Mailing Details" description="Tell us where to send official documents or welcome kits.">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">Street Address</label>
                <input
                  type="text"
                  required
                  value={formData.address}
                  onChange={(e) => handleChange('address', e.target.value)}
                  className={`${inputClass} ${fieldBorderClass(hasText(formData.address))}`}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">City</label>
                <input
                  type="text"
                  required
                  value={formData.city}
                  onChange={(e) => handleChange('city', e.target.value)}
                  className={`${inputClass} ${fieldBorderClass(hasText(formData.city))}`}
                />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">State / Province</label>
                <input
                  type="text"
                  required
                  value={formData.state}
                  onChange={(e) => handleChange('state', e.target.value)}
                  className={`${inputClass} ${fieldBorderClass(hasText(formData.state))}`}
                />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">Postal Code</label>
                <input
                  type="text"
                  required
                  value={formData.postal_code}
                  onChange={(e) => handleChange('postal_code', e.target.value)}
                  className={`${inputClass} ${fieldBorderClass(hasText(formData.postal_code))}`}
                />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">Country</label>
                <input
                  type="text"
                  required
                  value={formData.country}
                  onChange={(e) => handleChange('country', e.target.value)}
                  className={`${inputClass} ${fieldBorderClass(hasText(formData.country))}`}
                />
                </div>
              </div>
            </div>
          </ApplicationFormSection>

          <ApplicationFormSection title="Academic Background" description="We use this to make sure the program fits your academic goals.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">Home Institution</label>
                <input
                  type="text"
                  required
                  value={formData.institution}
                  onChange={(e) => handleChange('institution', e.target.value)}
                  className={`${inputClass} ${fieldBorderClass(hasText(formData.institution))}`}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">Current Level</label>
                <input
                  type="text"
                  required
                  value={formData.education_level}
                  onChange={(e) => handleChange('education_level', e.target.value)}
                  className={`${inputClass} ${fieldBorderClass(hasText(formData.education_level))}`}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">Field of Study / Major</label>
                <input
                  type="text"
                  required
                  value={formData.field_of_study}
                  onChange={(e) => handleChange('field_of_study', e.target.value)}
                  className={`${inputClass} ${fieldBorderClass(hasText(formData.field_of_study))}`}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">GPA</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="4"
                  required
                  value={formData.gpa}
                  onChange={(e) => handleChange('gpa', e.target.value)}
                  className={`${inputClass} ${fieldBorderClass(hasText(formData.gpa))}`}
                />
              </div>
            </div>
          </ApplicationFormSection>

          <ApplicationFormSection title="Motivation & Essays" description="A short narrative helps admissions and scholarships advocate for you.">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">Program Motivation</label>
                <textarea
                  required
                  value={formData.program_motivation}
                  onChange={(e) => handleChange('program_motivation', e.target.value)}
                  className={`${textareaClass} ${fieldBorderClass(hasText(formData.program_motivation))}`}
                  placeholder="Share why this program matters to you."
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">Personal Statement</label>
                <textarea
                  required
                  value={formData.motivation}
                  onChange={(e) => handleChange('motivation', e.target.value)}
                  className={`${textareaClass} ${fieldBorderClass(hasText(formData.motivation))}`}
                  placeholder="Anything else we should know about your goals, academics, or leadership."
                />
              </div>
            </div>
          </ApplicationFormSection>

          <ApplicationFormSection title="Photo ID" description="Upload a passport-style photo for your student ID and program materials.">
            <div className="space-y-3">
              <input
                type="file"
                accept="image/jpeg,image/png"
                required={!formData.passport_photo_key}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) {
                    handlePhotoUpload(file);
                  }
                }}
                className={`block w-full text-sm text-slate-600 dark:text-slate-200 file:mr-4 file:rounded-lg file:border-0 file:bg-emerald-600 file:px-4 file:py-2 file:text-sm file:font-semibold file:text-white hover:file:bg-emerald-700 border rounded-lg ${fieldBorderClass(hasPhoto)}`}
              />
              <div className="text-xs text-slate-500 dark:text-slate-400">JPG or PNG, max 5 MB.</div>
              {photoUploading && <div className="text-sm text-emerald-600">Uploading photo...</div>}
              {photoUploadError && <div className="text-sm text-red-600">{photoUploadError}</div>}
              {photoPreviewUrl && (
                <img
                  src={photoPreviewUrl}
                  alt="Passport upload preview"
                  className="mt-3 h-28 w-28 rounded-xl object-cover border border-slate-200 dark:border-slate-700"
                />
              )}
            </div>
          </ApplicationFormSection>

          <ApplicationFormSection title="Support & Emergency Contact" description="We keep this on file so advisors can support you before departure.">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">Special Requirements (dietary, housing, accessibility)</label>
                <textarea
                  required
                  value={formData.special_requirements}
                  onChange={(e) => handleChange('special_requirements', e.target.value)}
                  className={`${textareaClass} ${fieldBorderClass(hasText(formData.special_requirements))}`}
                  placeholder="Let us know if you need accommodations, medical support, etc."
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">Emergency Contact Name</label>
                  <input
                    type="text"
                    required
                    value={formData.emergency_contact_name}
                    onChange={(e) => handleChange('emergency_contact_name', e.target.value)}
                    className={`${inputClass} ${fieldBorderClass(hasText(formData.emergency_contact_name))}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase mb-1 text-slate-600 dark:text-slate-200">Relationship</label>
                  <input
                    type="text"
                    required
                    value={formData.emergency_contact_relationship}
                    onChange={(e) => handleChange('emergency_contact_relationship', e.target.value)}
                    className={`${inputClass} ${fieldBorderClass(hasText(formData.emergency_contact_relationship))}`}
                  />
                </div>
                <div className="md:col-span-2">
                  <PhoneField
                    label="Emergency Contact Phone"
                    labelClassName="text-xs font-semibold uppercase text-slate-600 dark:text-slate-200"
                    value={formData.emergency_contact_phone || undefined}
                    onChange={(value) => handleChange('emergency_contact_phone', value || '')}
                    inputClassName={`${inputClass} ${fieldBorderClass(Boolean(formData.emergency_contact_phone && isPhoneFormatValid(formData.emergency_contact_phone)))}`}
                    required
                    hideValidationMessage
                  />
                </div>
              </div>
            </div>
          </ApplicationFormSection>

          <ModalStepFooter
            confirmLabel="I confirm the application details are accurate and complete."
            confirmChecked={confirmSubmit}
            onConfirmChange={setConfirmSubmit}
            onCancel={onClose}
            onSave={onSaveDraft ? handleSaveDraft : undefined}
            saveLabel={draftSaving ? 'Saving…' : 'Save & Continue Later'}
            saveDisabled={draftSaving || submitting}
            onSubmit={handleSubmit}
            submitLabel={submitting ? 'Submitting…' : 'Submit Application'}
            submitDisabled={!confirmSubmit || submitting || !requiredComplete}
          />
        </form>
      </div>
    </div>
  );
};

const stageRequirementDetails: Record<string, Record<string, string>> = {
  visitor: {
    password_set: 'Set a password so you can log back in anytime from any device.',
    email_phone_verified: SMS_DISABLED
      ? 'Verify your email so we can send important updates and authenticate your account.'
      : 'Verify your email and phone so we can send important updates and authenticate your account.',
    program_interest: 'Tell us which program excites you most so we can personalize everything that follows.',
    commitment_level: 'Let us know how committed you are to studying abroad so we can tailor our guidance.',
    timeline: 'Share when you hope to depart so we can map your next steps.',
    program_motivation: 'Describe why you want to study abroad—this helps us champion your goals.'
  },
  prospect: {
    info_session_invite: 'Join the live Zoom session that showcases your program, meet advisors, and get your questions answered. Replays are available if you cannot attend live.',
    program_resource_portal: 'Explore the full program portal: why study abroad, what each core track delivers, optional trips, housing, safety, academics, and the exact roadmap to enrollment.',
    objection_playbook: 'Hear directly from alumni about their experience, outcomes, and why they chose this program.',
  },
  applicant: {
    pathway_selection: 'Choose one or more study tracks, then add any optional extensions.',
    application_fee: 'Pay the application fee to reserve your place in the review queue.',
    final_application: 'Use the “Open Application” button above to review the pre-filled application form, confirm your details, and submit it to lock your seat.',
  },
};

const MyApplication: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const [application, setApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState<any>({});
  const [error, setError] = useState<string | null>(null);
  const [pipelineStages] = usePipelineStages();
  const [viewedStage, setViewedStage] = useState<string | null>(null);
  const [readyFormData, setReadyFormData] = useState({
    selected_program_id: null as number | null,
    selected_cohort_ids: [] as number[],
    selected_extension_ids: [] as number[],
    pathway_selection_confirmed: false,
    program_interest: '',
    special_requirements: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    guardian_name: '',
    guardian_phone: '',
    guardian_email: '',
    guardian_relationship: '',
  });
  const [applicationModalOpen, setApplicationModalOpen] = useState(false);
  const [savingReadyForm, setSavingReadyForm] = useState(false);
  const [showQuestionnaireModal, setShowQuestionnaireModal] = useState(false);
  const [showAcceptedModal, setShowAcceptedModal] = useState(false);
  const [showAccountSetupModal, setShowAccountSetupModal] = useState(false);
  const [accountSetupMode, setAccountSetupMode] = useState<AccountSetupMode>('all');
  const [infoSessionModal, setInfoSessionModal] = useState(false);
  const [infoSessions, setInfoSessions] = useState<InfoSession[]>([]);
  const [infoSessionsLoading, setInfoSessionsLoading] = useState(false);
  const [infoSessionsError, setInfoSessionsError] = useState<string | null>(null);
  const [advisorThread, setAdvisorThread] = useState<AdvisorThread>({ messages: [] });
  const [advisorLoading, setAdvisorLoading] = useState(false);
  const [advisorError, setAdvisorError] = useState<string | null>(null);
  const [advisorDraft, setAdvisorDraft] = useState('');
  const [advisorSending, setAdvisorSending] = useState(false);
  const [showAdvisorEmojiPicker, setShowAdvisorEmojiPicker] = useState(false);
  const [advisorNewMessageNotice, setAdvisorNewMessageNotice] = useState<string | null>(null);
  const [advisorIsTyping, setAdvisorIsTyping] = useState(false);
  const [chatDebug, setChatDebug] = useState<{ socket: string; threadId?: number | null; last: string }>({
    socket: 'unknown',
    threadId: null,
    last: 'init',
  });
  const showChatDebug = process.env.NODE_ENV !== 'production';
  const advisorMessagesRef = useRef<HTMLDivElement | null>(null);
  const advisorInputRef = useRef<HTMLTextAreaElement | null>(null);
  const advisorEmojiPickerRef = useRef<HTMLDivElement | null>(null);
  const advisorSupportRef = useRef<HTMLDivElement | null>(null);
  const advisorTypingTimeoutRef = useRef<number | null>(null);
  const advisorTypingHideTimeoutRef = useRef<number | null>(null);
  const advisorTypingSentRef = useRef(false);
  const advisorTypingLastEmitAtRef = useRef(0);
  const advisorEmojiOptions = [
    '😀', '😁', '😂', '😊', '🙂', '😉', '😍', '🤩', '😎', '🥳',
    '🤔', '🙌', '👏', '👍', '👎', '🙏', '🤲', '💪', '❤️', '💚',
    '🔥', '✨', '🎉', '🎓', '📚', '📝', '💬', '📌', '✅', '⏳',
    '🌍', '✈️', '🕌', '🤝', '💡', '📈', '🎯', '🚀', '⭐', '🥰',
  ];
  const scrollAdvisorMessagesToBottom = useCallback(() => {
    if (!advisorMessagesRef.current) return;
    requestAnimationFrame(() => {
      if (!advisorMessagesRef.current) return;
      advisorMessagesRef.current.scrollTop = advisorMessagesRef.current.scrollHeight;
    });
  }, []);
  const scheduleAdvisorScrollToBottom = useCallback(() => {
    scrollAdvisorMessagesToBottom();
    window.setTimeout(scrollAdvisorMessagesToBottom, 80);
    window.setTimeout(scrollAdvisorMessagesToBottom, 180);
  }, [scrollAdvisorMessagesToBottom]);
  const [objectionModalOpen, setObjectionModalOpen] = useState(false);
  const [pathwayModalOpen, setPathwayModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [financingModalOpen, setFinancingModalOpen] = useState(false);
  const [partnerDiscount, setPartnerDiscount] = useState(0);
  const [partnerDiscountCurrency, setPartnerDiscountCurrency] = useState<string | null>(null);
  const [requirementModal, setRequirementModal] = useState<{ open: boolean; title: string; description?: string; key?: string }>({ open: false, title: '' });
  const [prospectStepCompletion, setProspectStepCompletion] = useState<Record<string, boolean>>({});
  const [finalApplicationComplete, setFinalApplicationComplete] = useState(false);
  const [submittingFinalApplication, setSubmittingFinalApplication] = useState(false);
  const [savingApplicationDraft, setSavingApplicationDraft] = useState(false);
  const [showUnderReviewModal, setShowUnderReviewModal] = useState(false);
  const prospectStepStorageKey = `prospectStepCompletion:${user?.id ?? 'anon'}:${application?.id ?? 'unknown'}`;
  const previousStageRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const stored = window.localStorage.getItem(prospectStepStorageKey);
      setProspectStepCompletion(stored ? JSON.parse(stored) : {});
    } catch {
      setProspectStepCompletion({});
    }
  }, [prospectStepStorageKey]);

  useEffect(() => {
    if (!application?.step_completion) return;
    const normalized: Record<string, boolean> = {};
    Object.entries(application.step_completion).forEach(([key, value]) => {
      if (typeof value === 'boolean') {
        normalized[key] = value;
      }
    });
    setProspectStepCompletion((prev) => ({ ...prev, ...normalized }));
  }, [application?.step_completion]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(prospectStepStorageKey, JSON.stringify(prospectStepCompletion));
    }
  }, [prospectStepCompletion, prospectStepStorageKey]);

  useEffect(() => {
    if (!user) return;
    partnerDiscountService.getEligibility()
      .then((summary) => {
        const best = summary.best_discount;
        setPartnerDiscount(best?.discount_value ?? 0);
        setPartnerDiscountCurrency(best?.discount_type === 'percent' ? '%' : (best?.currency ?? null));
      })
      .catch(() => {
        setPartnerDiscount(0);
        setPartnerDiscountCurrency(null);
      });
  }, [user]);

  useEffect(() => {
    if (typeof window === 'undefined' || !application?.id) return;
    const key = `finalApplicationComplete:${application.id}`;
    const stored = window.localStorage.getItem(key);
    setFinalApplicationComplete(stored === 'true');
  }, [application?.id]);

  useEffect(() => {
    if (typeof window === 'undefined' || !application?.id) return;
    const key = `finalApplicationComplete:${application.id}`;
    window.localStorage.setItem(key, finalApplicationComplete ? 'true' : 'false');
  }, [finalApplicationComplete, application?.id]);

  useEffect(() => {
    if (!application?.current_stage || pipelineStages.length === 0) {
      return;
    }
    const previousStage = previousStageRef.current;
    const currentStage = application.current_stage;
    if (previousStage) {
      const previousIndex = pipelineStages.findIndex(stage => stage.code === previousStage);
      const currentIndex = pipelineStages.findIndex(stage => stage.code === currentStage);
      if (previousIndex > -1 && currentIndex > -1 && previousIndex > currentIndex) {
        setProspectStepCompletion({});
        setFinalApplicationComplete(false);
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(prospectStepStorageKey);
          if (application.id) {
            window.localStorage.removeItem(`finalApplicationComplete:${application.id}`);
          }
        }
      }
    }
    if (currentStage !== 'applicant' && finalApplicationComplete) {
      setFinalApplicationComplete(false);
      if (typeof window !== 'undefined' && application.id) {
        window.localStorage.removeItem(`finalApplicationComplete:${application.id}`);
      }
    }
    if (typeof window !== 'undefined' && application.id) {
      if (currentStage !== 'applicant' || !finalApplicationComplete) {
        window.localStorage.removeItem(`underReviewNotice:${application.id}`);
      }
    }
    previousStageRef.current = currentStage;
  }, [application?.current_stage, application?.id, finalApplicationComplete, pipelineStages, prospectStepStorageKey]);

  const markProspectStepComplete = (key: string) => {
    setProspectStepCompletion(prev => {
      if (prev[key]) return prev;
      return { ...prev, [key]: true };
    });
    userService.setStepCompletion({ key, completed: true }).catch(() => {
      // Keep local completion even if request fails.
    });
  };
  const markProspectStepIncomplete = (key: string) => {
    setProspectStepCompletion(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    userService.setStepCompletion({ key, completed: false }).catch(() => {
      // Keep local state if request fails.
    });
  };

  const loadInfoSessions = async (): Promise<InfoSession[]> => {
    setInfoSessionsLoading(true);
    setInfoSessionsError(null);
    try {
      const sessions = await infoSessionService.listUpcoming();
      setInfoSessions(sessions);
      return sessions;
    } catch (err: any) {
      setInfoSessionsError(err?.response?.data?.error || 'Unable to load info sessions');
      return [];
    } finally {
      setInfoSessionsLoading(false);
    }
  };

  const loadAdvisorThread = useCallback(async () => {
    setAdvisorLoading(true);
    setAdvisorError(null);
    try {
      const thread = await advisorService.getThread();
      setAdvisorThread(thread);
      scheduleAdvisorScrollToBottom();
      if ((thread.unread_count_student || 0) > 0) {
        setAdvisorNewMessageNotice('New message from your advisor');
      }
    } catch (err: any) {
      setAdvisorError(err?.response?.data?.error || 'Unable to load advisor messages');
    } finally {
      setAdvisorLoading(false);
    }
  }, [scheduleAdvisorScrollToBottom]);

  const markAdvisorThreadRead = useCallback(async () => {
    try {
      await advisorService.markThreadRead();
      setAdvisorNewMessageNotice(null);
      setAdvisorThread((prev) => ({ ...prev, unread_count_student: 0 }));
      return true;
    } catch {
      // Keep unread indicator when backend did not confirm mark-read.
      return false;
    }
  }, []);

  const handleAdvisorSend = async () => {
    const body = advisorDraft.trim();
    if (!body) return;
    try {
      setAdvisorSending(true);
      setAdvisorError(null);
      await advisorService.sendMessage(body);
      if (advisorThread.id) {
        emitAdvisorStopTyping(advisorThread.id, () => {
          setChatDebug((prev) => ({ ...prev, last: 'emit stop_typing ack (send)' }));
        });
      }
      advisorTypingSentRef.current = false;
      advisorTypingLastEmitAtRef.current = 0;
      if (advisorTypingTimeoutRef.current) {
        window.clearTimeout(advisorTypingTimeoutRef.current);
        advisorTypingTimeoutRef.current = null;
      }
      setAdvisorDraft('');
      await loadAdvisorThread();
    } catch (err: any) {
      setAdvisorError(err?.response?.data?.error || 'Unable to send message right now.');
    } finally {
      setAdvisorSending(false);
    }
  };

  const handleAdvisorSectionViewed = useCallback(() => {
    if ((advisorThread.unread_count_student || 0) > 0) {
      void markAdvisorThreadRead();
    }
  }, [advisorThread.unread_count_student, markAdvisorThreadRead]);

  const handleInfoSessionRegister = async (sessionId: number) => {
    await infoSessionService.register(sessionId);
    await loadInfoSessions();
    markProspectStepComplete('info_session_invite');
    analyticsService.trackEvent({
      event_name: 'info_session_register',
      page_type: 'application',
      payload: {
        session_id: sessionId,
      },
    }).catch(() => {
      // Ignore analytics errors for info session registration.
    });
  };

  const handleInfoSessionCancel = async (sessionId: number) => {
    await infoSessionService.cancel(sessionId);
    const sessions = await loadInfoSessions();
    if (!sessions.some((session) => session.is_registered)) {
      markProspectStepIncomplete('info_session_invite');
    }
  };

  const handleApplicationSave = async (data: Record<string, string>) => {
    if (!application) throw new Error('Application not loaded yet.');
    setSavingApplicationDraft(true);
    try {
      await userService.updateMyApplication(data);
      await loadApplication();
    } finally {
      setSavingApplicationDraft(false);
    }
  };

  const handleFinalApplicationSubmit = async (data: Record<string, string>) => {
    if (!application) return;
    try {
      setSubmittingFinalApplication(true);
      await userService.updateMyApplication({ ...data, final_submit: true });
      await loadApplication();
      setFinalApplicationComplete(true);
      setApplicationModalOpen(false);
      analyticsService.trackEvent({
        event_name: 'application_submit',
        page_type: 'application',
        payload: {
          application_id: application.id,
        },
      }).catch(() => {
        // Ignore analytics errors for submission events.
      });
    } catch (error: any) {
      console.error('Failed to submit application', error);
      setError(error.response?.data?.error || 'Unable to submit application right now');
    } finally {
      setSubmittingFinalApplication(false);
    }
  };

  const handleApplicationModalOpen = (source: string) => {
    setApplicationModalOpen(true);
    analyticsService.trackEvent({
      event_name: 'apply_click',
      page_type: 'application',
      payload: {
        application_id: application?.id,
        source,
      },
    }).catch(() => {
      // Ignore analytics errors for apply clicks.
    });
    analyticsService.trackEvent({
      event_name: 'application_start',
      page_type: 'application',
      payload: {
        application_id: application?.id,
        source,
      },
    }).catch(() => {
      // Ignore analytics errors for application start events.
    });
  };

  const infoSessionRegistered = infoSessions.some((session) => session.is_registered);

  const checkRequirementCompletion = (application: Application, requirement: string): boolean => {
    if (!application) return false;
    switch (requirement) {
      case 'password_set':
        return Boolean(application.password_set);
      case 'email_phone_verified':
        return Boolean(application.email_verified && (SMS_DISABLED || application.phone_verified));
      case 'program_interest':
        return !!application.program_interest;
      case 'commitment_level':
        return !!application.commitment_level;
      case 'timeline':
        return !!application.timeline;
      case 'how_did_you_hear':
        return !!application.how_did_you_hear;
      case 'program_motivation':
        return !!application.program_motivation;
      case 'info_session_invite':
        return infoSessionRegistered || Boolean(prospectStepCompletion[requirement]);
      case 'program_resource_portal':
      case 'objection_playbook':
        return Boolean(prospectStepCompletion[requirement]);
      case 'final_application':
        return finalApplicationComplete;
      case 'pathway_selection':
        return Boolean(application.pathway_selection_confirmed);
      case 'application_fee':
        return Boolean(application.application_fee_paid);
      case 'special_requirements':
        return !!(application.special_requirements && application.special_requirements.trim());
      case 'emergency_contact':
        return !!(application.emergency_contact_name && application.emergency_contact_phone && application.emergency_contact_relationship);
      case 'additional_motivation':
        return !!application.motivation;
      default:
        return false;
    }
  };
  const [darkMode, setDarkMode] = useState(() => getStoredTheme());
  const [programOptions, setProgramOptions] = useState<Program[]>([]);
  const [pathwayPricing, setPathwayPricing] = useState<ProgramPricingResponse | null>(null);
  const [pathwayPricingLoading, setPathwayPricingLoading] = useState(false);
  const [highlightPlaybook, setHighlightPlaybook] = useState(false);

  useEffect(() => {
    loadApplication();
  }, []);

  useEffect(() => {
    if (application?.current_stage) {
      setViewedStage(application.current_stage);
    }
  }, [application?.current_stage]);

  useEffect(() => {
    if (!application?.id) return;
    if (application.application_status !== 'accepted') return;
    const stamp = application.accepted_at || 'accepted';
    const storageKey = `acceptedNotice:${application.id}:${stamp}`;
    if (window.localStorage.getItem(storageKey)) return;
    setShowAcceptedModal(true);
    window.localStorage.setItem(storageKey, 'seen');
  }, [application?.accepted_at, application?.application_status, application?.id]);

  useEffect(() => {
    if (application?.current_stage === 'prospect') {
      loadInfoSessions();
    }
  }, [application?.current_stage]);

  useEffect(() => {
    if (application?.id) {
      loadAdvisorThread();
    }
  }, [application?.id, loadAdvisorThread]);

  useEffect(() => {
    if (!user?.id) return;
    let interval: number | null = null;
    const unreadPollMs = process.env.NODE_ENV === 'production' ? 60000 : 5000;
    const syncUnread = async () => {
      try {
        const data = await advisorService.getUnreadCount();
        const unread = data.unread_count || 0;
        setAdvisorThread((prev) => ({ ...prev, unread_count_student: unread }));
        if (unread > 0) {
          setAdvisorNewMessageNotice('New message from your advisor');
        }
      } catch {
        // Keep socket as primary; polling is fallback only.
      }
    };
    syncUnread();
    interval = window.setInterval(syncUnread, unreadPollMs);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncUnread();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      if (interval) window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const client = connectAdvisorSocket();
    if (!client) return;
    setChatDebug((prev) => ({ ...prev, socket: client.connected ? 'connected' : 'connecting' }));
    const onConnect = () => setChatDebug((prev) => ({ ...prev, socket: 'connected', last: 'socket connected' }));
    const onDisconnect = (reason: string) =>
      setChatDebug((prev) => ({ ...prev, socket: `disconnected (${reason})`, last: 'socket disconnected' }));
    client.on('connect', onConnect);
    client.on('disconnect', onDisconnect);
    return () => {
      client.off('connect', onConnect);
      client.off('disconnect', onDisconnect);
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !advisorThread.id) return;
    joinAdvisorThreadRoom(advisorThread.id, (response) => {
      const ok = response?.ok ? 'ok' : `error:${response?.error || 'unknown'}`;
      setChatDebug((prev) => ({ ...prev, threadId: advisorThread.id, last: `join_thread ${ok} id=${advisorThread.id}` }));
    });
    return () => {
      emitAdvisorStopTyping(advisorThread.id as number);
      leaveAdvisorThreadRoom(advisorThread.id as number);
    };
  }, [advisorThread.id, user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const unsubscribeNew = onAdvisorMessageEvent((payload: any) => {
      if (!payload || !payload.thread_id) return;
      setAdvisorThread((prev) => {
        if (!prev.id || prev.id !== payload.thread_id) return prev;
        if (prev.messages.some((message) => message.id === payload.id)) return prev;
        const incomingFromAdvisor = payload.sender_role === 'admin';
        if (incomingFromAdvisor) {
          setAdvisorNewMessageNotice('New message from your advisor');
          if (advisorTypingHideTimeoutRef.current) {
            window.clearTimeout(advisorTypingHideTimeoutRef.current);
            advisorTypingHideTimeoutRef.current = null;
          }
          setAdvisorIsTyping(false);
        }
        setChatDebug((prev) => ({ ...prev, last: `chat:new role=${payload.sender_role} id=${payload.id}` }));
        return {
          ...prev,
          updated_at: payload.created_at || prev.updated_at,
          unread_count_student: incomingFromAdvisor
            ? (prev.unread_count_student || 0) + 1
            : (prev.unread_count_student || 0),
          messages: [...prev.messages, payload],
        };
      });
      scheduleAdvisorScrollToBottom();
    });

    const unsubscribeUpdated = onAdvisorThreadUpdatedEvent((payload: any) => {
      if (!payload) return;
      setChatDebug((prev) => ({ ...prev, last: `chat:thread_updated thread=${payload.thread_id}` }));
      loadAdvisorThread();
    });
    const unsubscribeTyping = onAdvisorTypingEvent((payload: any) => {
      if (!payload || !advisorThread.id) return;
      if (Number(payload.thread_id) !== Number(advisorThread.id)) return;
      if (payload.sender_role !== 'admin') return;
      if (advisorTypingHideTimeoutRef.current) {
        window.clearTimeout(advisorTypingHideTimeoutRef.current);
      }
      setAdvisorIsTyping(true);
      setChatDebug((prev) => ({ ...prev, last: `chat:typing role=${payload.sender_role}` }));
      advisorTypingHideTimeoutRef.current = window.setTimeout(() => {
        setAdvisorIsTyping(false);
      }, 2200);
    });
    const unsubscribeStopTyping = onAdvisorStopTypingEvent((payload: any) => {
      if (!payload || !advisorThread.id) return;
      if (Number(payload.thread_id) !== Number(advisorThread.id)) return;
      if (payload.sender_role !== 'admin') return;
      if (advisorTypingHideTimeoutRef.current) {
        window.clearTimeout(advisorTypingHideTimeoutRef.current);
      }
      advisorTypingHideTimeoutRef.current = window.setTimeout(() => {
        setAdvisorIsTyping(false);
      }, 450);
      setChatDebug((prev) => ({ ...prev, last: `chat:stop_typing role=${payload.sender_role}` }));
    });

    return () => {
      unsubscribeNew();
      unsubscribeUpdated();
      unsubscribeTyping();
      unsubscribeStopTyping();
    };
  }, [advisorThread.id, loadAdvisorThread, scheduleAdvisorScrollToBottom, user?.id]);

  useEffect(() => {
    return () => {
      if (advisorTypingTimeoutRef.current) {
        window.clearTimeout(advisorTypingTimeoutRef.current);
      }
      if (advisorTypingHideTimeoutRef.current) {
        window.clearTimeout(advisorTypingHideTimeoutRef.current);
      }
    };
  }, []);

  const handleAdvisorDraftChange = (value: string) => {
    setAdvisorDraft(value);
    if (!advisorThread.id) return;
    const trimmed = value.trim();
    if (!trimmed) {
      emitAdvisorStopTyping(advisorThread.id, () => {
        setChatDebug((prev) => ({ ...prev, last: 'emit stop_typing ack (empty)' }));
      });
      advisorTypingSentRef.current = false;
      advisorTypingLastEmitAtRef.current = 0;
      if (advisorTypingTimeoutRef.current) {
        window.clearTimeout(advisorTypingTimeoutRef.current);
        advisorTypingTimeoutRef.current = null;
      }
      return;
    }
    const now = Date.now();
    if (!advisorTypingSentRef.current || now - advisorTypingLastEmitAtRef.current > 900) {
      emitAdvisorTyping(advisorThread.id, (response) => {
        const ok = response?.ok ? 'ok' : 'no-ok';
        setChatDebug((prev) => ({ ...prev, last: `emit typing ack ${ok}` }));
      });
      advisorTypingSentRef.current = true;
      advisorTypingLastEmitAtRef.current = now;
    }
    if (advisorTypingTimeoutRef.current) {
      window.clearTimeout(advisorTypingTimeoutRef.current);
    }
    advisorTypingTimeoutRef.current = window.setTimeout(() => {
      if (!advisorThread.id) return;
      emitAdvisorStopTyping(advisorThread.id, () => {
        setChatDebug((prev) => ({ ...prev, last: 'emit stop_typing ack (timeout)' }));
      });
      advisorTypingSentRef.current = false;
      advisorTypingLastEmitAtRef.current = 0;
    }, 1200);
  };

  const handleAdvisorEmojiInsert = (emoji: string) => {
    handleAdvisorDraftChange(`${advisorDraft}${emoji}`);
    setShowAdvisorEmojiPicker(false);
    window.setTimeout(() => advisorInputRef.current?.focus(), 0);
  };

  const handleAdvisorComposerKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!advisorSending && advisorDraft.trim()) {
        void handleAdvisorSend();
      }
    }
  };

  useEffect(() => {
    if (!location.hash) return;
    if (location.hash === '#advisor-support') {
      const el = document.getElementById('advisor-support');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      handleAdvisorSectionViewed();
      return;
    }
    if (location.hash === '#program-playbook') {
      const el = document.getElementById('program-playbook');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightPlaybook(true);
        window.setTimeout(() => setHighlightPlaybook(false), 2400);
      }
    }
  }, [location.hash, application?.id, handleAdvisorSectionViewed]);

  useEffect(() => {
    const input = advisorInputRef.current;
    if (!input) return;
    input.style.height = 'auto';
    const nextHeight = Math.min(input.scrollHeight, 180);
    input.style.height = `${Math.max(nextHeight, 88)}px`;
    input.style.overflowY = input.scrollHeight > 180 ? 'auto' : 'hidden';
  }, [advisorDraft]);

  useEffect(() => {
    if (!showAdvisorEmojiPicker) return;
    const handleClickOutside = (event: MouseEvent) => {
      const picker = advisorEmojiPickerRef.current;
      if (!picker) return;
      if (!picker.contains(event.target as Node)) {
        setShowAdvisorEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAdvisorEmojiPicker]);

  useEffect(() => {
    const target = advisorSupportRef.current;
    if (!target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.45);
        if (visible) {
          handleAdvisorSectionViewed();
        }
      },
      { threshold: [0.45] }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [handleAdvisorSectionViewed]);

  useEffect(() => {
    scheduleAdvisorScrollToBottom();
  }, [advisorThread.messages.length, advisorThread.updated_at, scheduleAdvisorScrollToBottom]);

  useEffect(() => {
    if (application) {
      const toNumberArray = (value: unknown): number[] => (
        Array.isArray(value) ? value.filter((item) => typeof item === 'number') : []
      );
      setReadyFormData({
        selected_program_id: application.selected_program_id ?? null,
        selected_cohort_ids: toNumberArray(application.selected_cohort_ids),
        selected_extension_ids: toNumberArray(application.selected_extension_ids),
        pathway_selection_confirmed: application.pathway_selection_confirmed ?? false,
        program_interest: application.program_interest || '',
        special_requirements: application.special_requirements || '',
        emergency_contact_name: application.emergency_contact_name || '',
        emergency_contact_phone: application.emergency_contact_phone || '',
        guardian_name: application.guardian_name || '',
        guardian_phone: application.guardian_phone || '',
        guardian_email: application.guardian_email || '',
        guardian_relationship: application.guardian_relationship || '',
      });
    }
  }, [application]);

  useEffect(() => {
    applyTheme(darkMode);
  }, [darkMode]);

  useEffect(() => {
    const loadPrograms = async () => {
      try {
        const programs = await programService.getPrograms();
        setProgramOptions(programs || []);
      } catch (err) {
        console.error('Failed to load programs for questionnaire', err);
      }
    };
    loadPrograms();
  }, []);


  useEffect(() => {
    const stageForPricing = viewedStage ?? application?.current_stage;
    const useDraftPricing = stageForPricing === 'applicant'
      && (!viewedStage || viewedStage === application?.current_stage);
    const programId = useDraftPricing
      ? (readyFormData.selected_program_id ?? application?.selected_program_id ?? null)
      : (application?.selected_program_id ?? null);
    if (!programId) {
      setPathwayPricing(null);
      return;
    }
    let active = true;
    setPathwayPricingLoading(true);
    programService.getProgramPricing(programId)
      .then((data) => {
        if (!active) return;
        setPathwayPricing(data);
      })
      .catch((error) => {
        console.error('Failed to load program pricing for snapshot', error);
        if (!active) return;
        setPathwayPricing(null);
      })
      .finally(() => {
        if (!active) return;
        setPathwayPricingLoading(false);
      });
    return () => {
      active = false;
    };
  }, [application?.selected_program_id, application?.current_stage, readyFormData.selected_program_id, viewedStage]);

  const loadApplication = async () => {
    try {
      setLoading(true);
      const response = await userService.getMyApplication();
      setApplication(response.application);
    } catch (error: any) {
      console.error('Error loading application:', error);
      setError('Failed to load your application');
    } finally {
      setLoading(false);
    }
  };

  const handleAdvanceStage = async () => {
    if (!application) return;
    if (application.current_stage === 'visitor' && !application.password_set) {
      setAccountSetupMode('password');
      setShowAccountSetupModal(true);
      setError('Please set a password before moving to the next stage.');
      return;
    }
    
    try {
      setAdvancing(true);
      await userService.advanceMyApplication(`Self-advanced from ${application.current_stage}`);
      await loadApplication(); // Reload to get updated data
    } catch (error: any) {
      console.error('Error advancing stage:', error);
      setError(error.response?.data?.error || 'Failed to advance application');
    } finally {
      setAdvancing(false);
    }
  };

  const handleSaveProfile = async () => {
    if (!application) return;
    
    try {
      setAdvancing(true); // Reuse the same loading state
      await userService.updateMyApplication(editData);
      await loadApplication(); // Reload to get updated data
      setEditing(false);
      setEditData({});
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setError(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setAdvancing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const userTimeZone = (typeof Intl !== 'undefined' && Intl.DateTimeFormat)
    ? Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
    : 'UTC';
  const normalizeDate = (dateString: string) => {
    if (!dateString) return new Date();
    const hasZone = /[zZ]|([+-]\d{2}:?\d{2})$/.test(dateString);
    return new Date(hasZone ? dateString : `${dateString}Z`);
  };

  const formatDateTime = (dateString: string) => {
    const formatted = normalizeDate(dateString).toLocaleString('en-US', {
      timeZone: userTimeZone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      hour12: true,
      minute: '2-digit'
    });
    return formatted;
  };

  const formatStage = (stage: string) => {
    return stage.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatShortText = (value?: string, limit = 80) => {
    if (!value) return '';
    if (value.length <= limit) return value;
    return `${value.slice(0, limit).trim()}…`;
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleReadyFormChange = (
    field: keyof typeof readyFormData,
    value: string | number | number[] | null
  ) => {
    setReadyFormData(prev => ({ ...prev, [field]: value }));
  };

  const saveReadyForm = async (payload?: typeof readyFormData) => {
    if (!application) return;
    try {
      setSavingReadyForm(true);
      await userService.updateMyApplication(payload ?? readyFormData);
      await loadApplication();
    } catch (error: any) {
      console.error('Error updating ready stage form:', error);
      setError(error.response?.data?.error || 'Failed to save your details');
    } finally {
      setSavingReadyForm(false);
    }
  };

  const handleReadyFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await saveReadyForm();
  };

  const handleSubmitPathwaySelection = async () => {
    const payload = { ...readyFormData, pathway_selection_confirmed: true };
    setReadyFormData(payload);
    await saveReadyForm(payload);
  };

  const toggleDarkMode = () => setDarkMode(prev => !prev);

  const isMinor = (() => {
    if (!application?.date_of_birth) return false;
    const dob = new Date(application.date_of_birth);
    if (Number.isNaN(dob.getTime())) return false;
    const now = new Date();
    let age = now.getFullYear() - dob.getFullYear();
    const monthDiff = now.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
      age -= 1;
    }
    return age < 18;
  })();

  const handlePathwaySelection = useCallback((selection: {
    programId: number | null;
    trackIds: number[];
    addonIds: number[];
  }) => {
    const resolvedProgramId = selection.programId
      ?? readyFormData.selected_program_id
      ?? application?.selected_program_id
      ?? (programOptions.length === 1 ? programOptions[0].id : null);
    const programName = programOptions.find((program) => program.id === resolvedProgramId)?.title || '';
    setReadyFormData((prev) => ({
      ...prev,
      selected_program_id: resolvedProgramId,
      selected_cohort_ids: selection.trackIds,
      selected_extension_ids: selection.addonIds,
      pathway_selection_confirmed: false,
      program_interest: programName || prev.program_interest,
    }));
  }, [application?.selected_program_id, programOptions, readyFormData.selected_program_id]);

  const isApplicantStageView = (viewedStage ?? application?.current_stage) === 'applicant';
  const isViewingCurrentStageLocal = !viewedStage || viewedStage === application?.current_stage;
  const useDraftSelection = isApplicantStageView && isViewingCurrentStageLocal;
  const effectiveSelectedProgramId = useDraftSelection
    ? (readyFormData.selected_program_id ?? application?.selected_program_id ?? null)
    : (application?.selected_program_id ?? null);
  const selectedProgramById = effectiveSelectedProgramId
    ? (programOptions.find((program) => program.id === effectiveSelectedProgramId) || null)
    : null;
  const selectedProgram = application?.program_interest
    ? (programOptions.find(program => program.title === application.program_interest) || null)
    : null;
  const pricingPathways = [
    ...(pathwayPricing?.cohorts ?? []).map((item) => ({ ...item, pathway_type: 'track' as const })),
    ...(pathwayPricing?.extensions ?? []).map((item) => ({ ...item, pathway_type: 'addon' as const })),
    ...((pathwayPricing?.pathways ?? [])),
  ];
  const estimateDurationDays = (pathway: ProgramPricingPathway) => {
    if (pathway.duration_days != null) return pathway.duration_days;
    if (pathway.start_date && pathway.end_date) {
      const start = new Date(pathway.start_date);
      const end = new Date(pathway.end_date);
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
        const diff = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        return diff >= 0 ? diff + 1 : null;
      }
    }
    return null;
  };
  const estimatePrice = (pathway: ProgramPricingPathway) => (
    pathway.selling_price ?? pathway.estimated_selling_price ?? 0
  );
  const toNumberArray = (value: unknown): number[] => {
    if (!Array.isArray(value)) return [];
    return value
      .map((item) => (typeof item === 'number' ? item : Number(item)))
      .filter((item) => Number.isFinite(item));
  };
  const selectedTrackIds = useDraftSelection
    ? toNumberArray(readyFormData.selected_cohort_ids)
    : toNumberArray(application?.selected_cohort_ids);
  const selectedAddonIds = useDraftSelection
    ? toNumberArray(readyFormData.selected_extension_ids)
    : toNumberArray(application?.selected_extension_ids);
  const selectedTracks = pricingPathways
    .filter((pathway) => (pathway.pathway_type || 'track') === 'track')
    .filter((pathway) => pathway.id != null && selectedTrackIds.includes(pathway.id));
  const selectedAddons = pricingPathways
    .filter((pathway) => (pathway.pathway_type || 'track') === 'addon')
    .filter((pathway) => pathway.id != null && selectedAddonIds.includes(pathway.id));
  const totalDurationDays = [...selectedTracks, ...selectedAddons]
    .map((pathway) => estimateDurationDays(pathway) ?? 0)
    .reduce((sum, value) => sum + value, 0);
  const totalDurationSummary = (() => {
    if (!totalDurationDays) return '—';
    const weeks = Math.floor(totalDurationDays / 7);
    const extraDays = totalDurationDays % 7;
    if (weeks >= 1 && extraDays) {
      const weekLabel = weeks === 1 ? 'week' : 'weeks';
      return `${weeks} ${weekLabel} + ${extraDays} days`;
    }
    if (weeks >= 1) {
      const weekLabel = weeks === 1 ? 'week' : 'weeks';
      return `${weeks} ${weekLabel}`;
    }
    return `${totalDurationDays} days`;
  })();
  const rawTotalPrice = [...selectedTracks, ...selectedAddons]
    .map((pathway) => estimatePrice(pathway))
    .reduce((sum, value) => sum + value, 0);
  const discountAmount = (() => {
    if (!selectedProgramById || selectedTracks.length < 2) return 0;
    const amount = selectedProgramById.bundle_discount_amount ?? 0;
    return amount > 0 ? amount : 0;
  })();
  const appliedPartnerDiscount = (() => {
    if (!partnerDiscount) return 0;
    if (partnerDiscountCurrency === '%') {
      return rawTotalPrice * (partnerDiscount / 100);
    }
    const programCurrency = (selectedProgramById?.currency || pathwayPricing?.base_currency || '').toUpperCase();
    if (!programCurrency || !partnerDiscountCurrency) return partnerDiscount;
    return partnerDiscountCurrency.toUpperCase() === programCurrency ? partnerDiscount : 0;
  })();
  const totalPrice = Math.max(0, rawTotalPrice - discountAmount - appliedPartnerDiscount);
  const snapshotCurrency = selectedProgramById?.currency || pathwayPricing?.base_currency || 'USD';
  const formatSnapshotCurrency = (value: number) => new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: snapshotCurrency,
    maximumFractionDigits: 2,
  }).format(value);
  const snapshotLocation = [application?.city, application?.state, application?.country].filter(Boolean).join(', ');
  const currentStageCodeForHook = application?.current_stage ?? null;
  const stageHistoryStagesForHook = application?.stage_history?.map(t => t.to_stage) || [];
  const currentStageIndexForHook = currentStageCodeForHook
    ? pipelineStages.findIndex((stage) => stage.code === currentStageCodeForHook)
    : -1;
  const priorStageCodesForHook = currentStageIndexForHook >= 0
    ? pipelineStages.slice(0, currentStageIndexForHook + 1).map((stage) => stage.code)
    : [];
  const viewableStageCodesForHook = Array.from(new Set([
    ...stageHistoryStagesForHook,
    ...(currentStageCodeForHook ? [currentStageCodeForHook] : []),
    ...priorStageCodesForHook,
  ]));
  const displayStageCodeForHook = currentStageCodeForHook
    && viewedStage
    && viewableStageCodesForHook.includes(viewedStage)
    ? viewedStage
    : currentStageCodeForHook;
  const isApplicantUnderReview = displayStageCodeForHook === 'applicant'
    && finalApplicationComplete
    && Boolean(application?.application_fee_paid)
    && application?.application_status !== 'accepted';
  const underReviewNoticeKey = application?.id ? `underReviewNotice:${application.id}` : null;
  useEffect(() => {
    if (!isApplicantUnderReview || !underReviewNoticeKey) return;
    if (typeof window === 'undefined') return;
    if (window.localStorage.getItem(underReviewNoticeKey) === 'true') return;
    setShowUnderReviewModal(true);
    window.localStorage.setItem(underReviewNoticeKey, 'true');
  }, [isApplicantUnderReview, underReviewNoticeKey]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">No Application Found</h2>
          <p className="text-gray-600">Please contact support if you believe this is an error.</p>
        </div>
      </div>
    );
  }

  const currentStageCode = application.current_stage;
  const currentStageInfo = getStageInfo(currentStageCode, pipelineStages);
  const stageHistoryStages = application.stage_history?.map(t => t.to_stage) || [];
  const currentStageIndex = pipelineStages.findIndex(stage => stage.code === currentStageCode);
  const priorStageCodes = currentStageIndex >= 0
    ? pipelineStages.slice(0, currentStageIndex + 1).map((stage) => stage.code)
    : [];
  const viewableStageCodes = Array.from(new Set([
    ...stageHistoryStages,
    currentStageCode,
    ...priorStageCodes,
  ]));
  const displayStageCode: string = viewedStage && viewableStageCodes.includes(viewedStage)
    ? viewedStage
    : currentStageCode;

  if (!currentStageInfo) {
    // Debug log for troubleshooting unknown stage
    // eslint-disable-next-line no-console
    console.error('Unknown Stage:', {
      current_stage: application.current_stage,
      pipelineStages: pipelineStages.map(s => s.code)
    });
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Unknown Stage</h2>
          <p className="text-gray-600">Your application is in an unknown stage. Please contact support.</p>
        </div>
      </div>
    );
  }
  const displayStageInfo = getStageInfo(displayStageCode, pipelineStages) || currentStageInfo;
  const isViewingCurrentStage = displayStageCode === application.current_stage;
  const isReadyStage = displayStageInfo.code === 'applicant';
  const canEditVisitorForm = application.current_stage === 'visitor' && isViewingCurrentStage;
  const paymentsEnabled = env.REACT_APP_ENABLE_PAYMENTS === 'true';
  const requirementLabelFallback: Record<string, string> = {
    password_set: 'Set your password',
    email_phone_verified: SMS_DISABLED ? 'Verify email' : 'Verify email & phone',
    program_interest: 'Program interest',
    commitment_level: 'Commitment level',
    timeline: 'Timeline',
    how_did_you_hear: 'How did you hear about us?',
    program_motivation: 'Program motivation',
    info_session_invite: 'Join an info session',
    program_resource_portal: 'Review the program portal',
    objection_playbook: 'Hear from alumni',
    pathway_selection: 'Choose your pathway',
    application_fee: 'Pay the application fee',
    final_application: 'Submit your application',
    special_requirements: 'Special requirements',
    emergency_contact: 'Emergency contact',
    additional_motivation: 'Additional motivation',
  };
  const fallbackStageRequirements: Record<string, string[]> = {
    visitor: ['password_set', 'email_phone_verified', 'program_interest', 'commitment_level', 'timeline', 'how_did_you_hear', 'program_motivation'],
    prospect: ['info_session_invite', 'program_resource_portal', 'objection_playbook'],
    applicant: ['pathway_selection', 'final_application', 'application_fee'],
  };
  const resolveStageRequirements = (stageCode: string, stageInfo?: { requirements?: string[] }) => {
    const requirements = stageInfo?.requirements || [];
    if (requirements.length > 0) {
      if (stageCode === 'applicant') {
        const order = ['pathway_selection', 'final_application', 'application_fee'];
        const ordered = order.filter((req) => requirements.includes(req));
        const remaining = requirements.filter((req) => !ordered.includes(req));
        return [...ordered, ...remaining];
      }
      return requirements;
    }
    return fallbackStageRequirements[stageCode] || [];
  };
  const stageRequirements = resolveStageRequirements(displayStageInfo.code, displayStageInfo);
  const currentStageRequirements = resolveStageRequirements(currentStageInfo.code, currentStageInfo);
  const completedRequirements = stageRequirements.reduce((count: number, requirement: string) => (
    checkRequirementCompletion(application, requirement) ? count + 1 : count
  ), 0);
  const lastUpdatedDisplay = application.updated_at ? formatDateTime(application.updated_at) : 'Just now';
  const stageProgressPercent = stageRequirements.length ? Math.round((completedRequirements / stageRequirements.length) * 100) : null;
  const totalStages = pipelineStages.length;
  const stageIndex = pipelineStages.findIndex(stage => stage.code === displayStageInfo.code);
  const nextStageFromOrder = stageIndex >= 0 ? pipelineStages[stageIndex + 1] : null;
  const upcomingStageInfo = displayStageInfo.nextStage
    ? getStageInfo(displayStageInfo.nextStage, pipelineStages)
    : nextStageFromOrder;
  const completedStagesCount = new Set(stageHistoryStages).size;
  const sortedStageHistory = application.stage_history
    ? [...application.stage_history].sort(
        (a, b) => new Date(a.transition_date).getTime() - new Date(b.transition_date).getTime()
      )
    : [];
  const lastTimelineEntry = sortedStageHistory[sortedStageHistory.length - 1];
  const lastTimelineDisplay = lastTimelineEntry ? formatDateTime(lastTimelineEntry.transition_date) : 'Not yet recorded';
  const visitorChecklistComplete = application.current_stage !== 'visitor'
    ? true
    : (currentStageRequirements.length === 0
      ? true
      : currentStageRequirements.every((req: string) => checkRequirementCompletion(application, req)));
  const prospectChecklistComplete = application.current_stage !== 'prospect'
    ? true
    : (currentStageRequirements.length === 0
      ? true
      : currentStageRequirements.every((req: string) => checkRequirementCompletion(application, req)));
  const applicantChecklistComplete = application.current_stage !== 'applicant'
    ? true
    : (currentStageRequirements.length === 0
      ? true
      : currentStageRequirements.every((req: string) => checkRequirementCompletion(application, req)));
  const currentStageChecklistComplete = currentStageRequirements.length === 0
    ? true
    : currentStageRequirements.every((req: string) => checkRequirementCompletion(application, req));
  const mustSetPassword = application.current_stage === 'visitor' && !application.password_set;
  const canAdvance = isViewingCurrentStage
    && currentStageInfo
    && currentStageInfo.canSelfAdvance
    && currentStageChecklistComplete
    && !mustSetPassword;
  const canAdvanceFromReview = canAdvance && !isApplicantUnderReview;
  const currentActionText = currentStageInfo?.actionText
    || (application.current_stage === 'visitor' ? 'Complete Questionnaire' : 'Go to the Next Stage');
  const disabledActionClass = 'bg-slate-200 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700';
  const visitorQuestionnaireKeys = ['program_interest', 'commitment_level', 'timeline', 'how_did_you_hear', 'program_motivation'];
  const visitorQuestionnaireComplete = visitorQuestionnaireKeys.every((key) => checkRequirementCompletion(application, key));
  const visitorActionItems = [
    { key: 'password_set', label: 'Set your password', completed: checkRequirementCompletion(application, 'password_set') },
    { key: 'email_phone_verified', label: SMS_DISABLED ? 'Verify email' : 'Verify email & phone', completed: checkRequirementCompletion(application, 'email_phone_verified') },
    { key: 'questionnaire', label: 'Complete the short questionnaire', completed: visitorQuestionnaireComplete },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-emerald-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900 dark:text-slate-100">
      {/* Mobile Header */}
      <div className="sm:hidden sticky top-0 z-30 bg-white/90 dark:bg-slate-900/90 backdrop-blur border-b border-slate-200 dark:border-slate-700">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-600 dark:text-emerald-300">
              Stage {stageIndex + 1} / {pipelineStages.length}
            </p>
            <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
              {formatStageTitle(displayStageInfo.title)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                navigate('/student-pathway#advisor-support');
                markAdvisorThreadRead();
              }}
              className="relative inline-flex items-center justify-center h-9 w-9 rounded-full border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-200 bg-white/80 dark:bg-slate-800/80"
              aria-label="Open advisor messages"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h8m-8 4h5m-7 7l-4-4V5a2 2 0 012-2h16a2 2 0 012 2v12a2 2 0 01-2 2H8z" />
              </svg>
              {(advisorThread.unread_count_student || 0) > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-[10px] leading-[18px] text-white font-semibold">
                  {advisorThread.unread_count_student}
                </span>
              )}
            </button>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-200 bg-white/80 dark:bg-slate-800/80"
              aria-label="Go home"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-emerald-600 text-white"
              aria-label="Log out"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="relative border-b border-slate-200 bg-white/80 dark:bg-slate-900/80 backdrop-blur hidden sm:block">
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-emerald-600 dark:text-emerald-300">Stage {stageIndex + 1} / {pipelineStages.length}</p>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">{formatStageTitle(displayStageInfo.title)}</h1>
              <span className="px-2.5 py-1 text-xs rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200 border border-emerald-200/60 dark:border-emerald-800/70">
                {upcomingStageInfo ? `Next: ${formatStageTitle(upcomingStageInfo.title)}` : 'Waiting for review'}
              </span>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-200 mt-0.5">{displayStageInfo.description}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <div className="hidden sm:flex items-center gap-2 text-slate-500 dark:text-slate-200">
              <span>{lastTimelineDisplay}</span>
              <span className="text-slate-400">•</span>
              <span>{stageProgressPercent !== null ? `${stageProgressPercent}% complete` : 'Progress updating'}</span>
            </div>
            <button
              onClick={toggleDarkMode}
              aria-pressed={darkMode}
              className="inline-flex items-center px-3 py-1.5 border border-slate-300 dark:border-slate-700 rounded-full text-slate-600 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {darkMode ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
                )}
              </svg>
              {darkMode ? 'Light' : 'Dark'}
            </button>
            <button
              onClick={() => {
                navigate('/student-pathway#advisor-support');
                markAdvisorThreadRead();
              }}
              className="inline-flex items-center px-3 py-1.5 border border-slate-300 dark:border-slate-700 rounded-full text-slate-600 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition relative"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h8m-8 4h5m-7 7l-4-4V5a2 2 0 012-2h16a2 2 0 012 2v12a2 2 0 01-2 2H8z" />
              </svg>
              Messages
              {(advisorThread.unread_count_student || 0) > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-amber-500 text-white text-[11px] font-semibold">
                  {advisorThread.unread_count_student}
                </span>
              )}
            </button>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center px-3 py-1.5 border border-slate-300 dark:border-slate-700 rounded-full text-slate-600 dark:text-slate-200 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 transition"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              Home
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-full text-white bg-emerald-600 hover:bg-emerald-700 transition shadow-sm"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setError(null)}
                  className="text-red-400 hover:text-red-600"
                >
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Pipeline Progress */}
              <StudentProgress
                currentStage={application.current_stage}
                selectedStage={displayStageCode}
              className="animate-fade-in-up"
              onStageSelect={(stageCode) => {
                if (viewableStageCodes.includes(stageCode)) {
                  setViewedStage(stageCode);
                }
              }}
            />
            {/* Action Items */}
            <div className="glass-panel bg-white/85 dark:bg-slate-900/70 border border-slate-100 dark:border-slate-700 shadow-xl rounded-3xl overflow-hidden animate-fade-in-up animate-delay-100">
              <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Current Stage</p>
                    <p className="text-sm uppercase tracking-[0.3em] text-emerald-500">
                      {displayStageInfo.islamicTitle ? formatStageTitle(displayStageInfo.islamicTitle) : '—'}
                    </p>
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Next Stage</p>
                    <p className="text-sm uppercase tracking-[0.3em] text-emerald-500">
                      {upcomingStageInfo?.islamicTitle ? formatStageTitle(upcomingStageInfo.islamicTitle) : 'Complete'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="px-6 py-6">
                    <div className="flex items-start">
                      <div className="flex-1">
                        <h5 className="text-gray-600 dark:text-slate-200 mb-4">{displayStageInfo.description}</h5>
                    
                    
                    {displayStageCode === 'visitor' || resolveStageRequirements(displayStageCode, displayStageInfo).length ? (
                      <div className="mb-6 space-y-4">
                        <div className="rounded-2xl border border-slate-100 dark:border-slate-700 bg-white/70 dark:bg-slate-900/60 px-4 py-4">
                          <h4 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Next Action Items</h4>
                          {isApplicantUnderReview ? (
                            <div className="mt-4 rounded-xl border border-emerald-200/80 dark:border-emerald-700/60 bg-emerald-50/70 dark:bg-emerald-900/30 p-4">
                              <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">
                                Application received — under review.
                              </p>
                              <p className="text-xs text-emerald-700/80 dark:text-emerald-100/80 mt-2">
                                We’ve received your application and application fee. Our team is reviewing it now.
                              </p>
                            </div>
                          ) : (
                            <ul className="mt-4 space-y-3">
                              {(displayStageCode === 'visitor'
                                ? visitorActionItems
                                : (() => {
                                    const baseRequirements = resolveStageRequirements(displayStageCode, displayStageInfo);
                                    const labelMap = baseRequirements.reduce((acc: Record<string, string>, req: string, idx: number) => {
                                      acc[req] = displayStageInfo.requirementLabels?.[idx]
                                        || requirementLabelFallback[req]
                                        || req;
                                      return acc;
                                    }, {} as Record<string, string>);
                                    const requirements = baseRequirements
                                      .filter((requirement: string) => (
                                        displayStageCode !== 'applicant'
                                        || (requirement !== 'special_requirements' && requirement !== 'emergency_contact')
                                      ));
                                    const orderedRequirements = displayStageCode === 'prospect'
                                      ? [
                                          'info_session_invite',
                                          'program_resource_portal',
                                          ...requirements.filter((req: string) => (
                                            req !== 'info_session_invite' && req !== 'program_resource_portal'
                                          )),
                                        ]
                                      : requirements;
                                    return orderedRequirements.map((requirement: string) => ({
                                      key: requirement,
                                      label: labelMap[requirement] || requirement,
                                      completed: checkRequirementCompletion(application, requirement),
                                    }));
                                  })())
                                .map((item: { key: string; label: string; completed: boolean }) => {
                                const isCompleted = item.completed;
                                const isPaymentComingSoon = displayStageCode === 'applicant'
                                  && item.key === 'application_fee'
                                  && !paymentsEnabled;
                                const requirementText = item.label;
                                return (
                                  <li key={item.key}>
                                    <button
                                      type="button"
                                      id={item.key === 'objection_playbook' ? 'program-playbook' : undefined}
                                      onClick={() => {
                                        if (isPaymentComingSoon) return;
                                        if (displayStageCode === 'visitor') {
                                          if (item.key === 'password_set') {
                                            setAccountSetupMode('password');
                                            setShowAccountSetupModal(true);
                                          } else if (item.key === 'email_phone_verified') {
                                            setAccountSetupMode('verification');
                                            setShowAccountSetupModal(true);
                                          } else {
                                            setShowQuestionnaireModal(true);
                                          }
                                        } else if (displayStageCode === 'prospect' && item.key === 'info_session_invite') {
                                          setInfoSessionModal(true);
                                        } else if (displayStageCode === 'prospect' && item.key === 'program_resource_portal') {
                                          navigate('/student-pathway/program-portal');
                                        } else if (displayStageCode === 'prospect' && item.key === 'objection_playbook') {
                                          setObjectionModalOpen(true);
                                        } else if (displayStageCode === 'applicant' && item.key === 'pathway_selection') {
                                          setPathwayModalOpen(true);
                                        } else if (displayStageCode === 'applicant' && item.key === 'application_fee') {
                                          setPaymentModalOpen(true);
                                        } else if (displayStageCode === 'applicant' && item.key === 'final_application') {
                                          handleApplicationModalOpen('requirements_list');
                                        } else {
                                          const details = stageRequirementDetails[displayStageCode]?.[item.key];
                                          setRequirementModal({
                                            open: true,
                                            title: requirementText,
                                            description: details,
                                            key: displayStageCode === 'prospect' ? item.key : undefined
                                          });
                                        }
                                      }}
                                      aria-disabled={isPaymentComingSoon}
                                      className={`w-full h-10 flex items-center text-left space-x-3 rounded-md border px-3 py-2 focus:outline-none focus:ring-2 ${
                                        isPaymentComingSoon
                                          ? 'cursor-not-allowed border-slate-200 dark:border-slate-700 bg-slate-100 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400'
                                          : 'border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800 hover:border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-slate-900 dark:text-slate-200 focus:ring-emerald-500'
                                      } ${highlightPlaybook && item.key === 'objection_playbook' ? 'ring-2 ring-emerald-400 ring-offset-2 ring-offset-white dark:ring-offset-slate-900' : ''}`}
                                    >
                                      <span className="flex-shrink-0">
                                        {isCompleted ? (
                                          <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                          </svg>
                                        ) : (
                                          <span className={`inline-flex h-4 w-4 items-center justify-center rounded-full ${
                                            isPaymentComingSoon ? 'border border-slate-300 dark:border-slate-600' : 'border-2 border-emerald-300 dark:border-emerald-600'
                                          }`}></span>
                                        )}
                                      </span>
                                      <span className={`flex-1 text-sm truncate whitespace-nowrap ${isCompleted ? 'font-medium' : ''} ${isPaymentComingSoon ? 'text-slate-500 dark:text-slate-400' : 'text-slate-800 dark:text-slate-200'}`}>
                                        {requirementText}
                                      </span>
                                      {isPaymentComingSoon ? (
                                        <span className="ml-auto inline-flex items-center gap-2 text-xs font-semibold text-slate-500 dark:text-slate-400">
                                          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 text-[10px] dark:border-slate-600">
                                            ⏳
                                          </span>
                                          Coming soon
                                        </span>
                                      ) : (
                                        <svg className="ml-auto w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                        </svg>
                                      )}
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      </div>
                    ) : isReadyStage ? (
                      <div className="space-y-6 mb-6">
                        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div>
                              <p className="text-xs uppercase tracking-[0.35em] text-emerald-500">Step 1 · Choose your pathway</p>
                              <h4 className="text-base font-semibold text-slate-900 dark:text-white">Select your study track(s)</h4>
                              <p className="text-sm text-slate-600 dark:text-slate-300">
                                Pick the core track(s) you want, then add any optional extensions.
                              </p>
                            </div>
                            <div>
                              {readyFormData.selected_cohort_ids.length > 0 ? (
                                <span className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-800">
                                  Selection saved
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-white text-amber-700 border border-amber-200">
                                  Not selected
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="mt-4">
                            <PathwaySelectionBuilder
                              embedded
                              allowPublic
                              hideHeader
                              hideProgramSelect={programOptions.length <= 1}
                              autoShowSelection
                              selection={{
                                programId: readyFormData.selected_program_id,
                                trackIds: readyFormData.selected_cohort_ids,
                                addonIds: readyFormData.selected_extension_ids,
                              }}
                              onSelectionChange={handlePathwaySelection}
                            />
                          </div>
                        </div>

                        <div className="rounded-2xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800/40 p-4">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div>
                              <p className="text-xs uppercase tracking-[0.35em] text-amber-600">Step 2 · Complete your application</p>
                              <h4 className="text-base font-semibold text-slate-900 dark:text-white">Confirm your study abroad profile</h4>
                              <p className="text-sm text-slate-600 dark:text-slate-300">We pre-filled everything we know about you so far—just review and update.</p>
                            </div>
                            <div>
                              {finalApplicationComplete ? (
                                <span className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-800">
                                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 10-1.414-1.414L9 10.586 7.707 9.293a1 1 0 10-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  Application Submitted
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-3 py-1 text-xs font-medium rounded-full bg-white text-amber-700 border border-amber-200">
                                  Not submitted
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="mt-4 flex flex-wrap gap-3">
                            <button
                              type="button"
                              onClick={() => handleApplicationModalOpen('application_cta')}
                              className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700"
                            >
                              {finalApplicationComplete ? 'Review Application' : 'Open Application'}
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium border border-amber-200 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-900/30"
                            >
                              Download PDF Summary
                            </button>
                          </div>
                        </div>

                        <form onSubmit={handleReadyFormSubmit} className="space-y-4 rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
                          {isMinor && (
                            <div>
                              <p className="text-xs uppercase tracking-[0.3em] text-emerald-500 mb-1">Step 4 · Parent/Guardian</p>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase mb-1">
                                    Guardian Name
                                  </label>
                                  <input
                                    type="text"
                                    value={readyFormData.guardian_name}
                                    onChange={(e) => handleReadyFormChange('guardian_name', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                    placeholder="Full name"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase mb-1">
                                    Guardian Phone
                                  </label>
                                  <PhoneField
                                    value={readyFormData.guardian_phone || undefined}
                                    onChange={(value) => handleReadyFormChange('guardian_phone', value || '')}
                                    inputClassName="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                    hideValidationMessage
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase mb-1">
                                    Guardian Email
                                  </label>
                                  <input
                                    type="email"
                                    value={readyFormData.guardian_email}
                                    onChange={(e) => handleReadyFormChange('guardian_email', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                    placeholder="name@example.com"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-200 uppercase mb-1">
                                    Relationship
                                  </label>
                                  <input
                                    type="text"
                                    value={readyFormData.guardian_relationship}
                                    onChange={(e) => handleReadyFormChange('guardian_relationship', e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                    placeholder="Parent / Guardian"
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="flex justify-end">
                            <button
                              type="submit"
                              disabled={savingReadyForm}
                              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50"
                            >
                              {savingReadyForm ? 'Saving...' : 'Save Details'}
                            </button>
                          </div>
                        </form>
                      </div>
                    ) : (
                      <>
                        <div className="mb-6">
                          <h5 className="text-sm font-medium text-gray-900 dark:text-slate-100 mb-3">
                            {displayStageInfo.canSelfAdvance ? 'Ready for the next step? Complete these:' : 'Here\'s what\'s happening:'}
                          </h5>
                          <ul className="space-y-2">
                            {resolveStageRequirements(displayStageInfo.code, displayStageInfo).map((requirement: string, index: number) => {
                              const isCompleted = displayStageInfo.canSelfAdvance ? 
                                checkRequirementCompletion(application, requirement) : 
                                true;
                              const requirementText = displayStageInfo.requirementLabels?.[index]
                                || requirementLabelFallback[requirement]
                                || requirement;
                              
                              return (
                                <li key={index} className="flex items-start">
                                  <div className="flex-shrink-0 mt-1">
                                    {displayStageInfo.canSelfAdvance ? (
                                      isCompleted ? (
                                        <svg className="w-4 h-4 text-green-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                      ) : (
                                        <div className="w-4 h-4 border-2 border-gray-300 rounded mr-3"></div>
                                      )
                                    ) : (
                                      <svg className="w-4 h-4 text-blue-500 mr-3 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </div>
                                  <span className={`text-sm ${isCompleted ? 'text-slate-900 dark:text-slate-200 font-medium' : 'text-slate-800 dark:text-slate-200'} px-2 py-1 rounded bg-transparent`}>
                                    {requirementText}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        </div>

                        {upcomingStageInfo && (
                          <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg p-4 mb-4">
                            <h6 className="text-sm font-medium text-green-800 dark:text-green-200 mb-1">
                              Next Stage: {upcomingStageInfo.title}
                            </h6>
                            <p className="text-sm text-green-700 dark:text-green-100/80">
                              {upcomingStageInfo.description}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                </div>
              </div>
              <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-700 bg-slate-50/80 dark:bg-slate-900/40">
                {isViewingCurrentStage ? (
                  application.current_stage === 'visitor' ? (
                    <button
                      onClick={handleAdvanceStage}
                      disabled={!visitorChecklistComplete || advancing}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-slate-200 disabled:text-slate-600 disabled:border-slate-200 dark:disabled:bg-slate-800 dark:disabled:text-slate-300 dark:disabled:border-slate-700"
                    >
                      {advancing ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ) : (
                        <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
                          <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 001 1h1a1 1 0 001-1V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zM7 7a1 1 0 011-1h2a1 1 0 110 2H8a1 1 0 01-1-1zm0 3a1 1 0 011-1h2a1 1 0 110 2H8a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                      )}
                      {advancing ? 'Processing...' : 'Complete questionnaire items'}
                    </button>
                  ) : canAdvanceFromReview ? (
                    application.current_stage === 'prospect' ? (
                      <button
                        onClick={handleAdvanceStage}
                        disabled={!prospectChecklistComplete || advancing}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-slate-200 disabled:text-slate-600 disabled:border-slate-200 dark:disabled:bg-slate-800 dark:disabled:text-slate-300 dark:disabled:border-slate-700"
                      >
                        {advancing ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" clipRule="evenodd" />
                          </svg>
                        )}
                        {advancing ? 'Processing...' : currentActionText}
                      </button>
                    ) : (
                      <button
                        onClick={handleAdvanceStage}
                        disabled={application.current_stage === 'applicant'
                          ? (!applicantChecklistComplete || advancing || isApplicantUnderReview)
                          : advancing}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:cursor-not-allowed disabled:opacity-60 disabled:bg-slate-200 disabled:text-slate-600 disabled:border-slate-200 dark:disabled:bg-slate-800 dark:disabled:text-slate-300 dark:disabled:border-slate-700"
                      >
                        {advancing ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        ) : (
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h10a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                          </svg>
                        )}
                        {advancing ? 'Processing...' : currentActionText}
                      </button>
                    )
                  ) : (
                    <div className={`inline-flex items-center px-4 py-2 border text-sm font-medium rounded-md ${disabledActionClass}`}>
                      <svg className="w-4 h-4 mr-2 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.414-1.414L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      {isApplicantUnderReview ? 'Under review — next stage unlocked by admin' : currentActionText}
                    </div>
                  )
                ) : (
                  <div className="inline-flex flex-col sm:flex-row sm:items-center sm:space-x-3 px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-md bg-gray-50 dark:bg-slate-800/50 text-sm text-gray-700 dark:text-slate-200">
                    <span>You're viewing "{formatStageTitle(displayStageInfo.title)}" which has already been completed.</span>
                    <button
                      type="button"
                      onClick={() => setViewedStage(application.current_stage)}
                      className="mt-2 sm:mt-0 inline-flex items-center justify-center px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md"
                    >
                      Back to Current Stage
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Student Snapshot */}
            <div className="glass-panel bg-white/85 dark:bg-slate-900/70 border border-slate-100 dark:border-slate-700 rounded-3xl shadow-lg">
              <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Student Snapshot</h3>
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="flex items-center gap-4 pb-4 border-b border-slate-100 dark:border-slate-700">
                  {application.passport_photo_url ? (
                    <div className="w-16 h-16 rounded-full overflow-hidden ring-2 ring-emerald-300 dark:ring-emerald-700 ring-offset-2 ring-offset-white dark:ring-offset-slate-900">
                      <img
                        src={application.passport_photo_url}
                        alt={`${application.first_name} ${application.last_name}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200 flex items-center justify-center">
                      <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M5 20a7 7 0 0114 0" />
                      </svg>
                    </div>
                  )}
                  <div>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">
                      {application.first_name} {application.last_name}
                    </p>
                    <p className="text-xs uppercase tracking-widest text-emerald-600 dark:text-emerald-300">
                      {application.passport_photo_url ? 'Photo on file' : 'Photo pending'}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-slate-200 flex items-center gap-1">
                      {application.email}
                      {application.email_verified && (
                        <svg className="w-4 h-4 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                    </p>
                    {application.phone && (
                      <p className="text-sm text-gray-500 dark:text-slate-200 flex items-center gap-1">
                        {formatPhoneNumberForDisplay(application.phone)}
                        {application.phone_verified && (
                          <svg className="w-4 h-4 text-emerald-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </p>
                    )}
                  </div>
                </div>
                {editing ? (
                  /* Edit Form */
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Program Interest</label>
                      <select
                        value={editData.program_interest || ''}
                        onChange={(e) => setEditData({...editData, program_interest: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white dark:bg-slate-800 dark:text-slate-100"
                      >
                        <option value="">Select Program Interest</option>
                        {programOptions.map(program => (
                          <option key={program.id} value={program.title}>
                            {program.title}{program.location ? ` – ${program.location}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex space-x-3 pt-4">
                      <button
                        onClick={handleSaveProfile}
                        disabled={advancing}
                        className="flex-1 bg-emerald-600 text-white px-4 py-2 rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {advancing ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        onClick={() => {
                          setEditing(false);
                          setEditData({});
                        }}
                        className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Display Mode */
                  <>
                    {user?.is_admin ? (
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                        <div>
                          <dt className="text-sm font-medium text-gray-500 dark:text-slate-300">Lead Signals</dt>
                          <dd className="mt-1 text-sm text-gray-900 dark:text-slate-100">
                            {application.email_verified ? 'Email verified' : 'Email unverified'} · {application.phone_verified ? 'Phone verified' : 'Phone unverified'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500 dark:text-slate-300">Interest & Timeline</dt>
                          <dd className="mt-1 text-sm text-gray-900 dark:text-slate-100">
                            {application.program_interest ? formatStage(application.program_interest) : 'No program'} · {application.timeline ? formatStage(application.timeline) : 'No timeline'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500 dark:text-slate-300">Commitment Level</dt>
                          <dd className="mt-1 text-sm text-gray-900 dark:text-slate-100">
                            {application.commitment_level ? formatStage(application.commitment_level) : 'Not specified'}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500 dark:text-slate-300">Last Activity</dt>
                          <dd className="mt-1 text-sm text-gray-900 dark:text-slate-100">
                            {lastUpdatedDisplay}
                          </dd>
                        </div>
                      </dl>
                    ) : (
                      !finalApplicationComplete && (
                        <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2">
                          <div>
                            <dt className="text-sm font-medium text-gray-500 dark:text-slate-300">Program Interest</dt>
                            <dd className="mt-1 text-sm text-gray-900 dark:text-slate-100">
                              {application.program_interest ? formatStage(application.program_interest) : 'Not specified'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500 dark:text-slate-300">Program Destination</dt>
                            <dd className="mt-1 text-sm text-gray-900 dark:text-slate-100">
                              {selectedProgram?.location || 'Not specified'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500 dark:text-slate-300">Timeline</dt>
                            <dd className="mt-1 text-sm text-gray-900 dark:text-slate-100">
                              {application.timeline ? formatStage(application.timeline) : 'Not specified'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500 dark:text-slate-300">Last Updated</dt>
                            <dd className="mt-1 text-sm text-gray-900 dark:text-slate-100">
                              {lastUpdatedDisplay}
                            </dd>
                          </div>
                        </dl>
                      )
                    )}
                    <div className="mt-6 space-y-4">
                      <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 p-4">
                        <div className="flex items-center justify-between">
                          <p className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-300">Pathway & Pricing</p>
                          <span className="text-xs text-emerald-600 dark:text-emerald-300">
                            {application.pathway_selection_confirmed ? 'Confirmed' : 'In progress'}
                          </span>
                        </div>
                        {effectiveSelectedProgramId ? (
                          <div className="mt-3 space-y-3">
                            <div>
                              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                {selectedProgramById?.title || application.program_interest || 'Program not selected'}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-slate-300">
                                {selectedProgramById?.location || 'Location pending'}
                              </p>
                            </div>
                            {pathwayPricingLoading && (
                              <p className="text-xs text-slate-500 dark:text-slate-300">Loading pathway pricing…</p>
                            )}
                            {!pathwayPricingLoading && (selectedTracks.length > 0 || selectedAddons.length > 0) && (
                              <div className="flex flex-wrap gap-2">
                                {selectedTracks.map((track) => (
                                  <span
                                    key={`snapshot-track-${track.id}`}
                                    className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-xs font-medium dark:bg-emerald-900/40 dark:text-emerald-200"
                                  >
                                    {track.name || 'Core track'}
                                  </span>
                                ))}
                                {selectedAddons.map((addon) => (
                                  <span
                                    key={`snapshot-addon-${addon.id}`}
                                    className="inline-flex items-center rounded-full bg-blue-100 text-blue-700 px-3 py-1 text-xs font-medium dark:bg-blue-900/40 dark:text-blue-200"
                                  >
                                    {addon.name || 'Extension'}
                                  </span>
                                ))}
                              </div>
                            )}
                            {!pathwayPricingLoading && selectedTracks.length === 0 && selectedAddons.length === 0 && (
                              <p className="text-xs text-slate-500 dark:text-slate-300">No pathways selected yet.</p>
                            )}
                            <div className="grid grid-cols-2 gap-3 text-sm text-gray-700 dark:text-slate-200">
                              <div className="rounded-lg bg-white/70 dark:bg-slate-900/80 px-3 py-2">
                                <p className="text-[11px] uppercase text-slate-500 dark:text-slate-400">Total Duration</p>
                                <p className="font-semibold">{totalDurationSummary}</p>
                              </div>
                              <div className="rounded-lg bg-white/70 dark:bg-slate-900/80 px-3 py-2">
                                <p className="text-[11px] uppercase text-slate-500 dark:text-slate-400">Estimated Total</p>
                                <p className="font-semibold">
                                  {selectedTracks.length || selectedAddons.length ? formatSnapshotCurrency(totalPrice) : '—'}
                                </p>
                              </div>
                            </div>
                            {discountAmount > 0 && (
                              <div className="text-xs text-amber-700 dark:text-amber-200">
                                Bundle discount applied: -{formatSnapshotCurrency(discountAmount)}
                                {selectedProgramById?.bundle_discount_description ? ` · ${selectedProgramById.bundle_discount_description}` : ''}
                              </div>
                            )}
                            {appliedPartnerDiscount > 0 && (
                              <div className="text-xs text-emerald-700 dark:text-emerald-200">
                                Partner discount applied: -{formatSnapshotCurrency(appliedPartnerDiscount)}
                                {partnerDiscountCurrency === '%' ? ` (${partnerDiscount}%)` : ''}
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="mt-3 text-xs text-slate-500 dark:text-slate-300">
                            Select a program in Step 1 to see pathway pricing.
                          </p>
                        )}
                      </div>

                      {!finalApplicationComplete && (
                        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/60 p-4">
                          <p className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-300">Application Snapshot</p>
                          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-700 dark:text-slate-200">
                            {[
                              { label: 'Education Level', value: application.education_level ? formatStage(application.education_level) : null },
                              { label: 'Field of Study', value: application.field_of_study || null },
                              { label: 'Institution', value: application.institution || null },
                              { label: 'GPA', value: application.gpa || null },
                              { label: 'Location', value: snapshotLocation || null },
                              { label: 'Commitment', value: application.commitment_level ? formatStage(application.commitment_level) : null },
                              { label: 'Timeline', value: application.timeline ? formatStage(application.timeline) : null },
                            ]
                              .filter((item) => item.value)
                              .map((item) => (
                                <div key={item.label} className="rounded-lg bg-slate-50 dark:bg-slate-900/70 px-3 py-2">
                                  <p className="text-[11px] uppercase text-slate-500 dark:text-slate-400">{item.label}</p>
                                  <p className="font-semibold text-gray-900 dark:text-white">{item.value}</p>
                                </div>
                              ))}
                          </div>
                          {application.program_motivation && (
                            <div className="mt-3 rounded-lg bg-slate-50 dark:bg-slate-900/70 px-3 py-2 text-sm">
                              <p className="text-[11px] uppercase text-slate-500 dark:text-slate-400">Motivation</p>
                              <p className="font-medium text-gray-900 dark:text-white">{formatShortText(application.program_motivation, 120)}</p>
                            </div>
                          )}
                          {(application.emergency_contact_name || application.emergency_contact_phone || application.emergency_contact_relationship) && (
                            <div className="mt-3 rounded-lg bg-slate-50 dark:bg-slate-900/70 px-3 py-2 text-sm">
                              <p className="text-[11px] uppercase text-slate-500 dark:text-slate-400">Emergency Contact</p>
                              <p className="font-medium text-gray-900 dark:text-white">
                                {application.emergency_contact_name || 'Name pending'}
                                {application.emergency_contact_relationship ? ` · ${application.emergency_contact_relationship}` : ''}
                              </p>
                              {application.emergency_contact_phone && (
                                <p className="text-xs text-slate-500 dark:text-slate-300">
                                  {formatPhoneNumberForDisplay(application.emergency_contact_phone)}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Payments Summary */}
            <div className="glass-panel bg-white/85 dark:bg-slate-900/70 border border-slate-100 dark:border-slate-700 rounded-3xl shadow-lg">
              <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Payments</h3>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-4">
                  <p className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-300">Program Total</p>
                  <p className="mt-2 text-2xl font-semibold text-slate-900 dark:text-white">
                    {(selectedTracks.length || selectedAddons.length) ? formatSnapshotCurrency(totalPrice) : '—'}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm text-gray-700 dark:text-slate-200">
                  <div className="rounded-xl border border-emerald-100 dark:border-emerald-700/60 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-3">
                    <p className="text-[11px] uppercase text-emerald-700 dark:text-emerald-300">Paid</p>
                    <p className="font-semibold">{formatSnapshotCurrency(application.payments_total || 0)}</p>
                  </div>
                  <div className="rounded-xl border border-amber-100 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/30 px-3 py-3">
                    <p className="text-[11px] uppercase text-amber-700 dark:text-amber-200">Balance Due</p>
                    <p className="font-semibold">
                      {(selectedTracks.length || selectedAddons.length)
                        ? formatSnapshotCurrency(Math.max(0, totalPrice - (application.payments_total || 0)))
                        : '—'}
                    </p>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600 dark:text-slate-300">Application fee</span>
                    <span className="font-semibold text-slate-900 dark:text-white">
                      {application.application_fee_amount != null && application.application_fee_currency
                        ? new Intl.NumberFormat(undefined, { style: 'currency', currency: application.application_fee_currency }).format(application.application_fee_amount)
                        : '—'}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-300">
                    Status: {application.application_fee_paid ? 'Paid' : 'Not paid'}
                  </p>
                  <button
                    type="button"
                    onClick={() => setPaymentModalOpen(true)}
                    disabled={!paymentsEnabled}
                    className={`mt-3 w-full rounded-lg px-4 py-2 text-sm font-semibold ${
                      paymentsEnabled
                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                        : 'bg-slate-200 text-slate-600 cursor-not-allowed dark:bg-slate-800 dark:text-slate-300'
                    }`}
                  >
                    {paymentsEnabled ? 'Pay application fee' : (
                      <span className="inline-flex items-center justify-center gap-2">
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-300 text-[10px] text-slate-500 dark:border-slate-600 dark:text-slate-400">
                          ⏳
                        </span>
                        Coming soon
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Journey Insights */}
            <div className="glass-panel bg-white/85 dark:bg-slate-900/70 border border-slate-100 dark:border-slate-700 rounded-3xl shadow-lg">
              <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Journey Insights</h3>
              </div>
              <div className="px-6 py-5 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-2xl border border-emerald-100 dark:border-emerald-700/60 bg-emerald-50 dark:bg-emerald-900/30 p-4">
                    <p className="text-xs uppercase text-emerald-700 dark:text-emerald-300 tracking-widest">Stages Completed</p>
                    <p className="mt-2 text-2xl font-semibold text-emerald-900 dark:text-white">{completedStagesCount}/{totalStages}</p>
                  </div>
                  <div className="rounded-2xl border border-blue-100 dark:border-blue-700/60 bg-blue-50 dark:bg-blue-900/30 p-4">
                    <p className="text-xs uppercase text-blue-700 dark:text-blue-200 tracking-widest">Next Stage</p>
                    <p className="mt-2 text-base font-semibold text-blue-900 dark:text-white">{upcomingStageInfo ? upcomingStageInfo.title : 'Awaiting counselor'}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-300">
                    <span>Recent Updates</span>
                    <span>{sortedStageHistory.length ? `${sortedStageHistory.length} total` : 'No history yet'}</span>
                  </div>
                  <div className="flow-root">
                    <ul className="space-y-3">
                      {sortedStageHistory.slice(-3).map((transition, idx) => {
                        const toStageInfo = getStageInfo(transition.to_stage, pipelineStages);
                        const toLabel = toStageInfo?.title?.split(' – ')[0] || formatStage(transition.to_stage);
                        return (
                          <li key={idx} className="pl-3 border-l-2 border-emerald-200 dark:border-emerald-700/70">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-semibold text-gray-900 dark:text-slate-100">{toLabel}</span>
                              <span className="text-xs text-gray-500 dark:text-slate-300">{formatDate(transition.transition_date)}</span>
                            </div>
                            {transition.notes && (
                              <p className="mt-1 text-xs text-gray-500 dark:text-slate-300">{transition.notes}</p>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-300 mb-1">
                    <span>Checklist Progress</span>
                    <span>{stageProgressPercent !== null ? `${stageProgressPercent}%` : 'N/A'}</span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-700">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-teal-500"
                      style={{ width: `${stageProgressPercent ?? 0}%` }}
                    ></div>
                  </div>
                </div>
                <div className="bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-2xl p-4">
                  <p className="text-xs uppercase text-slate-500 dark:text-slate-300 tracking-widest">Latest Timeline Update</p>
                  <p className="text-sm font-medium text-slate-900 dark:text-white mt-1">{lastTimelineDisplay}</p>
                  {lastTimelineEntry && (
                    <p className="text-xs text-slate-500 dark:text-slate-300 mt-1">Advanced to {formatStage(lastTimelineEntry.to_stage)}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Advisor Support */}
            <div id="advisor-support" ref={advisorSupportRef} className="glass-panel bg-white/85 dark:bg-slate-900/70 border border-slate-100 dark:border-slate-700 rounded-3xl shadow-lg">
              <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">Advisor Support</h3>
                  {(advisorThread.unread_count_student || 0) > 0 && (
                    <span className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-semibold">
                      {advisorThread.unread_count_student} new
                    </span>
                  )}
                </div>
                <span className="text-xs uppercase tracking-widest text-emerald-500">Always here</span>
              </div>
              <div className="px-6 py-5 space-y-4">
                {advisorNewMessageNotice && (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                    {advisorNewMessageNotice}
                  </div>
                )}
                <p className="text-sm text-slate-600 dark:text-slate-200">
                  Need help or want feedback on your answers? Reach out to your Pathway Advisor and we’ll review your progress together.
                </p>
                <div className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/40 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-300">Messages</p>
                    <button
                      type="button"
                      onClick={async () => {
                        await loadAdvisorThread();
                        await markAdvisorThreadRead();
                      }}
                      className="text-xs font-semibold text-emerald-600 hover:text-emerald-700"
                    >
                      Refresh
                    </button>
                  </div>
                  {advisorLoading ? (
                    <p className="text-sm text-slate-500 dark:text-slate-300">Loading messages...</p>
                  ) : advisorThread.messages.length === 0 ? (
                    <p className="text-sm text-slate-600 dark:text-slate-200">No messages yet. Send a note and your advisor team will reply here.</p>
                  ) : (
                    <div ref={advisorMessagesRef} onScroll={handleAdvisorSectionViewed} className="space-y-2 max-h-[50vh] sm:max-h-80 overflow-y-auto pr-1">
                      {advisorThread.messages.map((message) => {
                        const isStudent = message.sender_role === 'student';
                        const senderLabel = isStudent ? 'You' : (message.sender_name || 'Advisor Team');
                        const timestamp = message.created_at ? new Date(message.created_at).toLocaleString() : '';
                        return (
                          <div key={message.id} className={`flex ${isStudent ? 'justify-end' : 'justify-start'}`}>
                            <div
                              className={`max-w-[86%] rounded-xl px-3 py-2 text-sm leading-6 border ${
                                isStudent
                                  ? 'bg-emerald-600 text-white border-emerald-500'
                                  : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border-slate-200 dark:border-slate-700'
                              }`}
                            >
                              <p className={`text-[11px] font-semibold mb-1 ${isStudent ? 'text-emerald-100' : 'text-slate-500 dark:text-slate-300'}`}>
                                {timestamp ? `${senderLabel} • ${timestamp}` : senderLabel}
                              </p>
                              <p className="whitespace-pre-wrap break-words">{message.body}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {advisorError && (
                    <p className="text-xs text-red-500">{advisorError}</p>
                  )}
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-slate-600 dark:text-slate-200">Message your advisor</label>
                    <div className="relative" ref={advisorEmojiPickerRef}>
                      <textarea
                        ref={advisorInputRef}
                      value={advisorDraft}
                      onChange={(e) => handleAdvisorDraftChange(e.target.value)}
                      onKeyDown={handleAdvisorComposerKeyDown}
                      onFocus={handleAdvisorSectionViewed}
                      rows={3}
                        className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 pr-12 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="Ask a question, request feedback, or share an update..."
                      />
                      <button
                        type="button"
                        onClick={() => setShowAdvisorEmojiPicker((prev) => !prev)}
                        className="absolute bottom-2 right-2 inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-300 dark:border-slate-600 bg-white/90 dark:bg-slate-900/90 text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                        title="Add emoji"
                        aria-label="Add emoji"
                      >
                        🙂
                      </button>
                      {showAdvisorEmojiPicker && (
                        <div className="absolute bottom-12 right-0 z-20 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg p-2">
                          <div className="grid grid-cols-5 gap-1">
                            {advisorEmojiOptions.map((emoji) => (
                              <button
                                key={emoji}
                                type="button"
                                onClick={() => handleAdvisorEmojiInsert(emoji)}
                                className="h-8 w-8 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
                                aria-label={`Insert ${emoji}`}
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {advisorIsTyping && (
                      <p className="text-xs text-emerald-600 dark:text-emerald-300 inline-flex items-center gap-2">
                        <span>Advisor is typing</span>
                        <span className="inline-flex items-center gap-1">
                          <span className="h-1.5 w-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="h-1.5 w-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '120ms' }} />
                          <span className="h-1.5 w-1.5 rounded-full bg-current animate-bounce" style={{ animationDelay: '240ms' }} />
                        </span>
                      </p>
                    )}
                    {showChatDebug && (
                      <p className="text-[10px] text-slate-400 dark:text-slate-500 font-mono">
                        debug socket:{chatDebug.socket} thread:{chatDebug.threadId ?? '-'} last:{chatDebug.last}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={handleAdvisorSend}
                      disabled={advisorSending || !advisorDraft.trim()}
                      className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {advisorSending ? 'Sending...' : 'Send Message'}
                    </button>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="button"
                    className="flex-1 inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-200 dark:hover:bg-emerald-900/30"
                  >
                    Schedule a Call
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AccountSetupModal
        open={showAccountSetupModal}
        onClose={() => {
          setShowAccountSetupModal(false);
          setAccountSetupMode('all');
        }}
        application={application}
        canEdit={canEditVisitorForm}
        onSaved={loadApplication}
        userId={user?.id}
        mode={accountSetupMode}
      />
      <DreamingQuestionnaireModal
        open={showQuestionnaireModal}
        onClose={() => setShowQuestionnaireModal(false)}
        application={application}
        canEdit={canEditVisitorForm}
        onSaved={loadApplication}
        onAdvance={handleAdvanceStage}
        canAdvanceStage={canAdvance}
        programOptions={programOptions}
      />
      <RequirementDetailsModal
        open={requirementModal.open}
        onClose={() => setRequirementModal({ open: false, title: '' })}
        title={requirementModal.title}
        description={requirementModal.description}
        onComplete={requirementModal.key ? () => markProspectStepComplete(requirementModal.key as string) : undefined}
        completed={requirementModal.key ? Boolean(prospectStepCompletion[requirementModal.key]) : undefined}
      />
      <InfoSessionModal
        open={infoSessionModal}
        onClose={() => setInfoSessionModal(false)}
        onCompleted={() => markProspectStepComplete('info_session_invite')}
        sessions={infoSessions}
        loading={infoSessionsLoading}
        error={infoSessionsError}
        onRegister={handleInfoSessionRegister}
        onCancel={handleInfoSessionCancel}
      />
      {showUnderReviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={() => setShowUnderReviewModal(false)} />
          <div className="relative w-full max-w-xl rounded-3xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-700 p-6">
            <div className="flex items-start gap-4">
              <div className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
                ✓
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white">Application received — under review</h3>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  We’ve received your application and application fee. Our team is reviewing your submission now.
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                  You’ll be promoted to the next stage once the review is complete.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setShowUnderReviewModal(false)}
                className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
      {showAcceptedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm" onClick={() => setShowAcceptedModal(false)} />
          <div className="relative w-full max-w-2xl rounded-3xl bg-white dark:bg-slate-900 shadow-2xl border border-emerald-200 dark:border-emerald-700 p-8">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-2xl font-semibold">
                ✓
              </div>
              <h3 className="text-2xl font-semibold text-slate-900 dark:text-white">Congratulations!</h3>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                You’ve been accepted into the program. Next, complete your enrollment deposit to secure your seat.
              </p>
              <button
                type="button"
                onClick={() => setShowAcceptedModal(false)}
                className="mt-2 inline-flex items-center px-5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
      <ApplicationFormModal
        open={applicationModalOpen}
        onClose={() => setApplicationModalOpen(false)}
        application={application}
        onSubmit={handleFinalApplicationSubmit}
        submitting={submittingFinalApplication}
        onSaveDraft={handleApplicationSave}
        draftSaving={savingApplicationDraft}
        confirmed={finalApplicationComplete}
      />
      <PaymentModal
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        onPaid={loadApplication}
      />
      <FinancingModal
        open={financingModalOpen}
        onClose={() => setFinancingModalOpen(false)}
        onCompleted={() => {}}
      />
      <PathwaySelectionModal
        open={pathwayModalOpen}
        onClose={() => setPathwayModalOpen(false)}
        onSave={saveReadyForm}
        onSubmitSelection={handleSubmitPathwaySelection}
        saving={savingReadyForm}
        confirmed={Boolean(readyFormData.pathway_selection_confirmed)}
        programOptions={programOptions}
        selection={{
          programId: readyFormData.selected_program_id,
          trackIds: readyFormData.selected_cohort_ids,
          addonIds: readyFormData.selected_extension_ids,
        }}
        onSelectionChange={handlePathwaySelection}
      />
      {objectionModalOpen && (
        <ObjectionToolkitModal
          open={objectionModalOpen}
          onClose={() => setObjectionModalOpen(false)}
          onCompleted={() => markProspectStepComplete('objection_playbook')}
          programId={application?.selected_program_id ?? readyFormData.selected_program_id ?? undefined}
        />
      )}
    </div>
  );
};

const PathwaySelectionModal: React.FC<PathwaySelectionModalProps> = ({
  open,
  onClose,
  onSave,
  onSubmitSelection,
  saving,
  confirmed,
  programOptions,
  selection,
  onSelectionChange,
}) => {
  const [confirmSubmit, setConfirmSubmit] = useState(false);

  useEffect(() => {
    if (open) {
      setConfirmSubmit(confirmed);
    }
  }, [open, confirmed]);

  if (!open) return null;

  const hasSelection = selection.trackIds.length > 0 || selection.addonIds.length > 0;
  const canSubmit = hasSelection && confirmSubmit && !saving;

  const handleSaveLater = async () => {
    await onSave();
    onClose();
  };

  const handleSubmit = async () => {
    await onSubmitSelection();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-5xl overflow-hidden rounded-3xl bg-slate-50/90 dark:bg-slate-900/95 shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4 dark:border-slate-700">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-emerald-500 mb-2">Stage 3 · Pathway Selection</p>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Choose your pathway</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">Select your core track(s) and any optional extensions.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition"
            aria-label="Close pathway selection"
          >
            ✕
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
          <PathwaySelectionBuilder
            embedded
            allowPublic
            hideHeader
            hideProgramSelect={programOptions.length <= 1}
            autoShowSelection
            selection={selection}
            onSelectionChange={onSelectionChange}
          />
        </div>
        <ModalStepFooter
          confirmLabel="I confirm these pathway selections are correct."
          confirmChecked={confirmSubmit}
          onConfirmChange={setConfirmSubmit}
          warning={hasSelection ? null : 'Select at least one pathway or add-on to submit.'}
          onCancel={onClose}
          onSave={handleSaveLater}
          saveLabel={saving ? 'Saving...' : 'Save & Continue Later'}
          saveDisabled={saving}
          onSubmit={handleSubmit}
          submitLabel="Submit Selection"
          submitDisabled={!canSubmit}
        />
      </div>
    </div>
  );
};

interface RequirementDetailsModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  onComplete?: () => void;
  completed?: boolean;
}

const RequirementDetailsModal: React.FC<RequirementDetailsModalProps> = ({ open, onClose, title, description, onComplete, completed }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-900/60" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-xl w-full p-6 animate-fade-in-up">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-emerald-500 mb-1">Stage Checklist</p>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">{title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            ✕
          </button>
        </div>
        <p className="mt-4 text-sm text-slate-700 dark:text-slate-200 whitespace-pre-line">
          {description || 'Your advisor will guide you through this step.'}
        </p>
        <div className="mt-6 flex justify-end gap-3">
          {onComplete && (
            <button
              type="button"
              onClick={() => {
                onComplete();
                onClose();
              }}
              disabled={completed}
              className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
            >
              {completed ? 'Completed' : 'Mark as Reviewed'}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-200 dark:hover:bg-emerald-900/30"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};
export default MyApplication;
const InfoSessionModal: React.FC<InfoSessionModalProps> = ({
  open,
  onClose,
  onCompleted,
  sessions,
  loading,
  error,
  onRegister,
  onCancel
}) => {
  const [submittingId, setSubmittingId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) {
      setSubmittingId(null);
    }
  }, [open]);

  if (!open) return null;

  const formatSessionTime = (session: InfoSession) => {
    const date = new Date(session.starts_at);
    try {
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: session.timezone || undefined,
        timeZoneName: 'short'
      }).format(date);
    } catch {
      return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      }).format(date);
    }
  };

  const handleRegister = async (sessionId: number) => {
    setSubmittingId(sessionId);
    try {
      await onRegister(sessionId);
      onCompleted();
    } finally {
      setSubmittingId(null);
    }
  };

  const handleCancel = async (sessionId: number) => {
    setSubmittingId(sessionId);
    try {
      await onCancel(sessionId);
    } finally {
      setSubmittingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-2xl w-full p-6 space-y-6 animate-fade-in-up">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-emerald-500 mb-2">Stage 2 · Info Session</p>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Reserve Your Live Session</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">Pick an upcoming session and we’ll send you a reminder before it starts.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>

        <div className="border border-emerald-100 dark:border-emerald-900/50 rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold text-slate-900 dark:text-white">Upcoming Sessions</h4>
            {loading && <span className="text-xs text-slate-500">Loading...</span>}
          </div>
          {error && (
            <div className="text-sm text-red-600">{error}</div>
          )}
          {!loading && !sessions.length && (
            <div className="text-sm text-slate-500">No sessions are scheduled right now. Check back soon.</div>
          )}
          <div className="space-y-3">
            {sessions.map((session) => {
              const seatsRemaining = session.seats_remaining;
              const isFull = session.is_full;
              const isRegistered = session.is_registered;
              return (
                <div key={session.id} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{session.title}</p>
                      <p className="text-xs text-slate-500">{formatSessionTime(session)}</p>
                    </div>
                    {seatsRemaining !== null && (
                      <span className="text-xs text-slate-500">
                        {isFull ? 'Full' : `${seatsRemaining} seats left`}
                      </span>
                    )}
                  </div>
                  {session.description && (
                    <p className="text-xs text-slate-500">{session.description}</p>
                  )}
                  <div className="flex flex-wrap items-center gap-2">
                    {isRegistered ? (
                      <>
                        <a
                          href={session.join_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700"
                        >
                          Join Session
                        </a>
                        <button
                          type="button"
                          onClick={() => handleCancel(session.id)}
                          disabled={submittingId === session.id}
                          className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-200"
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleRegister(session.id)}
                        disabled={isFull || submittingId === session.id}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {isFull ? 'Full' : 'Reserve spot'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ObjectionToolkitModal: React.FC<ObjectionToolkitModalProps> = ({ open, onClose, onCompleted, programId }) => {
  const formatDisplayName = (name?: string) => {
    if (!name) return 'Program Alumni';
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'Program Alumni';
    if (parts.length === 1) return parts[0];
    const first = parts[0];
    const lastInitial = parts[parts.length - 1][0]?.toUpperCase() || '';
    return lastInitial ? `${first} ${lastInitial}.` : first;
  };
  const getInitials = (value: string) => value.split(' ').map((part) => part[0]).join('').slice(0, 2);
  const [testimonials, setTestimonials] = useState<Array<{
    id: number;
    name?: string;
    role_title?: string;
    quote?: string;
    story?: string;
    countries?: string[];
    photo_url?: string;
  }>>([]);
  const [testimonialsLoading, setTestimonialsLoading] = useState(false);
  const [testimonialsError, setTestimonialsError] = useState<string | null>(null);
  const [testimonialIndex, setTestimonialIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [completionSent, setCompletionSent] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTestimonialsLoading(true);
    setTestimonialsError(null);
    testimonialService
      .getApproved(programId ?? undefined)
      .then((data) => setTestimonials(Array.isArray(data) ? data : []))
      .catch(() => setTestimonialsError('Unable to load testimonials right now.'))
      .finally(() => setTestimonialsLoading(false));
  }, [open, programId]);

  useEffect(() => {
    if (!open) return;
    setTestimonialIndex(0);
    setCompletionSent(false);
  }, [open, testimonials.length]);

  if (!open) return null;

  const totalTestimonials = testimonials.length;
  const clampIndex = (next: number) => (totalTestimonials === 0 ? 0 : (next + totalTestimonials) % totalTestimonials);
  const goPrev = () => {
    if (totalTestimonials > 1) setTestimonialIndex((prev) => clampIndex(prev - 1));
  };
  const goNext = () => {
    if (totalTestimonials > 1) setTestimonialIndex((prev) => clampIndex(prev + 1));
  };

  const handleComplete = () => {
    if (!completionSent) {
      onCompleted();
      setCompletionSent(true);
    }
    onClose();
  };

  const handleDismiss = () => {
    if (!completionSent) {
      onCompleted();
      setCompletionSent(true);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={handleDismiss} />
      <div className="relative bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-3xl w-full p-6 space-y-6 animate-fade-in-up max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-emerald-500 mb-2">Stage 2 · Alumni Insights</p>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Peer Testimonials</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">Hear directly from alumni who completed this program.</p>
          </div>
          <button onClick={handleDismiss} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>

        <div className="relative">
          <div
            className="overflow-hidden rounded-3xl border border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-900/50"
            onTouchStart={(event) => setTouchStartX(event.touches[0].clientX)}
            onTouchEnd={(event) => {
              if (touchStartX == null) return;
              const delta = touchStartX - event.changedTouches[0].clientX;
              setTouchStartX(null);
              if (Math.abs(delta) < 40) return;
              if (delta > 0) goNext();
              else goPrev();
            }}
          >
            {testimonialsLoading ? (
              <div className="p-6 text-sm text-slate-500 dark:text-slate-300">Loading testimonials...</div>
            ) : testimonialsError ? (
              <div className="p-6 text-sm text-rose-500">{testimonialsError}</div>
            ) : totalTestimonials === 0 ? (
              <div className="p-6 text-sm text-slate-500 dark:text-slate-300">
                No approved testimonials yet. Check back soon.
              </div>
            ) : (
              <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${testimonialIndex * 100}%)` }}
              >
                {testimonials.map((item) => {
                  const displayName = formatDisplayName(item.name);
                  const detail = item.role_title || 'Alumni';
                  const quoteText = item.quote || item.story || 'Testimonial pending details.';
                  return (
                    <div key={item.id} className="min-w-full p-6">
                      <p className="text-base text-slate-700 dark:text-slate-200 leading-7">{quoteText}</p>
                      <div className="mt-5 flex items-center gap-4">
                        {item.photo_url ? (
                          <img
                            src={item.photo_url}
                            alt={displayName}
                            className="h-12 w-12 rounded-full object-cover border border-slate-200 dark:border-slate-700"
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-12 w-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-semibold">
                            {getInitials(displayName)}
                          </div>
                        )}
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{displayName}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{detail}</p>
                        </div>
                      </div>
                      {item.countries?.length ? (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {item.countries.map((country) => (
                            <span
                              key={`${item.id}-${country}`}
                              className="inline-flex items-center rounded-full border border-emerald-200/70 bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-900/30 dark:text-emerald-100"
                            >
                              {country}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {totalTestimonials > 1 && !testimonialsLoading && !testimonialsError && (
            <>
              <button
                type="button"
                onClick={goPrev}
                className="absolute -left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/90 text-slate-600 dark:text-slate-200 shadow"
                aria-label="Previous testimonial"
              >
                ‹
              </button>
              <button
                type="button"
                onClick={goNext}
                className="absolute -right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full border border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/90 text-slate-600 dark:text-slate-200 shadow"
                aria-label="Next testimonial"
              >
                ›
              </button>

              <div className="mt-4 flex items-center justify-center gap-2">
                {testimonials.map((_, idx) => (
                  <button
                    key={`dot-${idx}`}
                    type="button"
                    onClick={() => setTestimonialIndex(idx)}
                    className={`h-2.5 w-2.5 rounded-full transition ${
                      idx === testimonialIndex ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'
                    }`}
                    aria-label={`Go to testimonial ${idx + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="rounded-2xl border border-emerald-200/70 bg-emerald-50/60 p-4 text-sm text-emerald-900 dark:bg-emerald-900/20 dark:text-emerald-100">
          Want to hear more? Your advisor can connect you with an alumni ambassador.
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleComplete}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium"
          >
            I’ve Reviewed Everything
          </button>
        </div>
      </div>
    </div>
  );
};

const FinancingModal: React.FC<FinancingModalProps> = ({ open, onClose, onCompleted }) => {
  if (!open) return null;

  const handleComplete = () => {
    onCompleted();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-3xl w-full p-6 space-y-6 animate-fade-in-up max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.35em] text-emerald-500 mb-2">Stage 2 · Funding Support</p>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white">Invest in Your Dream</h3>
            <p className="text-sm text-slate-600 dark:text-slate-300">Compare costs, unlock scholarships, and map a payment plan that fits.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:bg-amber-900/20 dark:border-amber-900/40">
            <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-2">Price Comparison Report</h4>
            <p className="text-xs text-amber-900/80 dark:text-amber-100">See how our tuition + housing stack up against similar programs (save up to 18%).</p>
            <button className="mt-3 inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-amber-900 bg-white hover:bg-amber-100 dark:text-amber-100 dark:bg-transparent dark:border dark:border-amber-500">
              Download Brochure
            </button>
          </div>
          <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
            <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">Payment Installment Plan</h4>
            <ul className="text-xs text-slate-600 dark:text-slate-300 space-y-1">
              <li>• Lock your seat with $100 application deposit.</li>
              <li>• 6 monthly payments starting 60 days before departure.</li>
              <li>• No-interest autopay + flexible extensions.</li>
            </ul>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
          <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-2">Scholarships & Aid</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { name: 'Global Leaders Scholarship', amount: '$1,000', due: 'Apr 15' },
              { name: 'STEM Abroad Grant', amount: '$750', due: 'May 01' },
              { name: 'Community Impact Award', amount: '$500', due: 'Apr 28' },
              { name: 'Faith & Service Fund', amount: '$1,200', due: 'May 10' },
            ].map((scholarship) => (
              <div key={scholarship.name} className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
                <p className="text-sm font-semibold text-slate-900 dark:text-white">{scholarship.name}</p>
                <p className="text-xs text-slate-500">{scholarship.amount} · Deadline {scholarship.due}</p>
                <button className="mt-2 inline-flex items-center px-3 py-1 text-xs font-medium text-emerald-600 hover:text-emerald-700">
                  Apply Now →
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50 dark:bg-slate-900/40">
          <h4 className="text-sm font-semibold text-slate-900 dark:text-white mb-1">Federal/State Aid Friendly</h4>
          <p className="text-xs text-slate-600 dark:text-slate-300">Use existing Pell Grants, state scholarships, or employer tuition benefits to offset your program cost (we provide the forms).</p>
          <button className="mt-3 inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-200 dark:hover:bg-emerald-900/30">
            Open Federal Aid Guide
          </button>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-700 dark:text-slate-200"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleComplete}
            className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium"
          >
            Funding Plan Saved
          </button>
        </div>
      </div>
    </div>
  );
};
